import { Quaternion } from 'three';

import { hasMatchAreaBounds, type MatchAreaBounds } from '@sb/ai/matchArea';
import {
  SAM2_SHADOW_MARGIN_FRACTION,
  bytesToBase64,
  cosineSimilarityHeatmap,
  fitOrientedBlobs,
  fp16ToFp32,
  fp32ArrayToFp16Bytes,
  nonMaxSuppression,
  PatchTensor,
  rasterizeHeatmapFp16,
  thresholdAndConnectedComponents,
  uint8BytesToUint16,
  unletterboxBlob,
  unletterboxOrientedBlob,
  derivePointPrompts,
  deriveHeatmapSamples,
  DEFAULT_NEGATIVE_WEIGHT,
  isUnpinnedModelVersion,
  CLICKFIND_ERROR_MESSAGES,
} from '@sb/dinofast';
import type { CartesianPose, Plane } from '@sb/geometry';
import {
  applyCompoundPose,
  getPlanePose,
  invertPose,
  movePlaneInItsZDirection,
  ZERO_POSE,
} from '@sb/geometry';
import type { Camera } from '@sb/integrations/implementations/Camera/implementation/Camera';
import type { CameraConfiguration } from '@sb/integrations/implementations/Camera/types';
import type { GPUTaskRunner } from '@sb/integrations/implementations/GPUTaskRunner/implementation/GPUTaskRunner';
import type { WristCamera } from '@sb/integrations/implementations/WristCamera/implementation/WristCamera';
import type { WristCameraConfiguration } from '@sb/integrations/implementations/WristCamera/types';
import type {
  CameraIntegration,
  CameraIntrinsics,
  CameraIntrinsicsExtended,
  ExtrinsicsKinematics,
} from '@sb/integrations/types/cameraTypes';
import { makeNamespacedLog } from '@sb/log';
import {
  ExtrinsicsKind,
  GraspFrame,
  LocateHeadKind,
  PoseEstimationMethod,
  PrimitiveShapeType,
} from '@sb/messenger/auto_generated/messenger/vision_bot_service';
import type {
  Blob2D as ProtoBlob2D,
  RunProjectionRequest,
  Run3DPoseEstimationRequest,
  CameraParams,
  Detection3D,
  RunCheckpointBasedInferenceResponse as ProtoInferenceResponse,
  ComputeDinoEmbeddingsResponse,
  ComputeDinoImageEmbeddingResponse,
  LocateHeadProto,
  SegmentMaskResponse,
  TrainLocateHeadResponse,
  PreloadRoboflowWorkflowResponse,
} from '@sb/messenger/auto_generated/messenger/vision_bot_service';
import type {
  LocateMethod,
  RegionOfInterest,
  RoutineRunnerState,
  VisionInterface,
  Space,
  EquipmentInterface,
} from '@sb/routine-runner';
import {
  DEFAULT_RANSAC_DISTANCE_THRESHOLD,
  DEFAULT_RANSAC_MAX_ITERATIONS,
  DEFAULT_RANSAC_MIN_SAMPLES,
  type ProjectionMethod,
} from '@sb/routine-runner/Step/Locate/Arguments';
import { isNotUndefined } from '@sb/utilities';

import { getCalibrationOffset } from './calibrationHelpers';
import {
  applyDisableRotation,
  getCameraColorFrame,
  resolveVisionExtrinsics,
} from './helpers';
import type {
  Blob2D,
  Detection2D,
  DinoFastLocateHead,
  DinoFastParams,
  LocateMatch,
  LocateResult,
  ResultOrdering,
  TextPromptDetection2DParams,
} from './LocateTypes';
import {
  RunCheckpointBasedInferenceResponse,
  RunRoboflowWorkflowDetectionResponse,
} from './LocateTypes';
import {
  cameraPoseAtCapture,
  describeMatchAreaBounds,
  isMatchWithinAreaBounds,
  makeMatchAreaFilter,
  raisedPlane,
  surfacePlaneAtPose,
  type MatchAreaProjection,
} from './matchAreaMetric';
import { poseToDisplayFrame } from './poseToDisplayFrame';
import { applyPostProjectionOrdering, sortBlobs } from './resultOrdering';
import type { Detection3DPoseEstimationConfig } from './visionConfigSchemas';
import type {
  DinoFastDenseHeatmap,
  OrientationReferenceRejection,
  OrientationSource,
} from './VisionMethodRunnerTypes';

const log = makeNamespacedLog('runLocate');

/**
 * How many detections a locate returns when the caller passes no explicit
 * `resultsLimit`. The count is no longer operator-configurable — the skill
 * wizard and the standalone Locate step both write a fixed 50 — and this is the
 * fallback for any caller (e.g. a preview) that sets none. It also bounds
 * projection cost: only this many blobs are projected into 3D poses.
 */
export const LOCATE_DEFAULT_RESULT_LIMIT = 50;

export const stripSegmentForLog = <T extends { segment?: Blob2D | null }>(
  item: T,
): T =>
  item.segment
    ? {
        ...item,
        segment: { ...item.segment, maskRle: undefined, contour: undefined },
      }
    : item;

/**
 * How many times `resultsLimit` a depth locate projects when it has match size
 * bounds to apply afterwards.
 */
const DEPTH_AREA_PROJECTION_HEADROOM = 2;

/**
 * We allow users to define a tranform to offset locate results by a set amount relative to the plane.
 * This applies that transform to a blob
 */
function applyUserDefinedTransform(
  transform: CartesianPose,
  blobRelativeToBase: CartesianPose,
  planePoseRelativeToBase: CartesianPose,
): CartesianPose {
  // The user-defined tranform is relative to the plane, so get the
  // blobs in plane coordinates first, then apply the transform
  // and then transform back to base coordinates
  const blobRelativeToPlane = applyCompoundPose(
    blobRelativeToBase,
    invertPose(planePoseRelativeToBase),
  );

  // apply the rotation separately so it doesn't change the position
  const rotation = new Quaternion(
    blobRelativeToPlane.i,
    blobRelativeToPlane.j,
    blobRelativeToPlane.k,
    blobRelativeToPlane.w,
  );

  const offsetRotation = new Quaternion(
    transform.i,
    transform.j,
    transform.k,
    transform.w,
  );

  const newRotation = offsetRotation.multiply(rotation);

  const offsetBlob = {
    x: blobRelativeToPlane.x + transform.x,
    y: blobRelativeToPlane.y + transform.y,
    z: blobRelativeToPlane.z + transform.z,
    i: newRotation.x,
    j: newRotation.y,
    k: newRotation.z,
    w: newRotation.w,
  };

  const offsetBlobRelativeToBase = applyCompoundPose(
    offsetBlob,
    planePoseRelativeToBase,
  );

  return offsetBlobRelativeToBase;
}

function getCalibrationCorrection(
  pose: CartesianPose,
  calibration: Space.AccuracyCalibrationEntry[],
): CartesianPose {
  if (calibration.length === 0) {
    log.info('getCalibrationCorrection.none', 'No calibration data found');

    return ZERO_POSE;
  }

  const calibrationOffset = getCalibrationOffset(pose, calibration);

  log.info(
    'getCalibrationCorrection.offset',
    'Calibration offset',
    calibrationOffset,
  );

  return calibrationOffset;
}

interface RunLocateArgs {
  camera: {
    equipment: Camera | WristCamera | undefined;
    integration: CameraIntegration | undefined;
  };
  /** Set by editor previews. See `preview` in vision_bot_service.proto. */
  preview?: boolean;
  regionOfInterest: RegionOfInterest;
  method: LocateMethod;
  plane?: Plane;
  resultsLimit: number | undefined;
  resultOrdering?: ResultOrdering;
  transform: CartesianPose | null;
  /**
   * Grasp frame for the locate result: defines the base in-plane orientation
   * of the returned pose. Sent to vision-bot as the `grasp_frame` field on the
   * plane-projection request, where vision-bot resolves the finished pose
   * orientation:
   *   - `'object'` / `undefined` → the detection principal axis (default).
   *   - `'plane'` → the taught plane X axis.
   *   - `'world'` → the world X axis projected onto the plane.
   * The user-defined `transform` offset always rides in the returned pose's own
   * display frame, regardless of grasp frame.
   */
  graspFrame?: 'object' | 'plane' | 'world';
  /**
   * Canonical orientation frame from teach time, when the taught shape
   * supported a reliable, non-symmetric frame. Rides into the plane-projection
   * request's `orientation_reference` field; vision-bot registers each detected
   * contour against it (object grasp frame) to resolve the 180° flip. Null/
   * undefined ⇒ the field is left unset and vision-bot falls back to plane.
   */
  orientationReference?: { referenceContour: number[][] } | null;
  accuracyCalibration: Space.AccuracyCalibrationEntry[] | null;
  vision: VisionInterface;
  objectHeight: number;
  getState: () => RoutineRunnerState;
  equipment: EquipmentInterface;
  intrinsics: CameraIntrinsics | CameraIntrinsicsExtended | undefined;
  extrinsics: ExtrinsicsKinematics | undefined;
  gpuTaskRunner?: GPUTaskRunner;
  /**
   * How each detection becomes a pose. Defaults to 'ManualPlane' when omitted,
   * which is what every caller predating the discriminator meant. 'Detection3D'
   * diverts to `run2DMethodWith3DProjection` (its own RPC) and requires
   * `detection3dConfig` + `depthImage`; the other three go to plane projection.
   */
  projectionMethod?: ProjectionMethod;
  ransacParams?: {
    maxIterations?: number;
    distanceThreshold?: number;
    minSamples?: number;
  };
  depthImage?: ArrayBuffer;
  /**
   * The arm pose to pair with this call's frames, sampled by the caller.
   *
   * Comes from `captureDepthWithPose` alongside `depthImage`: that frame is
   * captured before `runLocate` is entered, so a pose sampled in here would
   * already trail the depth capture. Omit it — as the callers that fetch no
   * depth frame do — and this function samples its own, which is the closer
   * match when it fetches every frame it uses.
   */
  wristPoseAtCapture?: CartesianPose;
  includeDebugVisualization?: boolean;
  maxTiltAngle?: number;
  /** DetectedPlane only — reject a surface fit below this inlier ratio. */
  minInlierRatio?: number;
  /** DetectedPlane only — emit a top-down grasp instead of dropping a rejected fit. */
  fallbackToTopDown?: boolean;
  disablePlaneOrientation?: boolean;
  /**
   * Pose-estimation config for `projectionMethod: 'Detection3D'`, which
   * `run2DMethodWith3DProjection` throws without. Distinct from
   * `method.kind === 'Detection3D'`, the routine Locate step's own text-prompt
   * 3D detection *method*, which carries its own config.
   */
  detection3dConfig?: Detection3DPoseEstimationConfig;
  /**
   * DinoFast preview only: also return the dense score heatmap from the 2D
   * detection pass (teach-preview overlay), so the routine editor's ClickFind
   * Locate step renders the same heatmap as the Teach page. Default/undefined ⇒
   * the heatmap is not computed-for-return (routine execution path).
   */
  returnHeatmap?: boolean;
}

/**
 * Shift a LocateMatch from cropped-ROI space back into full-frame space. The
 * detector box center (`detection.x/y`) AND, when a segment is present, every
 * `segment.contour` point are in ROI-local coords after detecting on a cropped
 * image — both must be translated by `+offsetX/+offsetY` so the match stays in
 * full-frame coordinates. The segment's RLE mask (if any) is left untouched:
 * downstream consumers only read the contour from a producer's segment.
 */
function shiftMatchByOffset(
  match: LocateMatch,
  offsetX: number,
  offsetY: number,
): LocateMatch {
  const shiftedSegment = match.segment
    ? {
        ...match.segment,
        contour: match.segment.contour.map((p) => ({
          x: p.x + offsetX,
          y: p.y + offsetY,
        })),
      }
    : null;

  return {
    detection: {
      ...match.detection,
      x: match.detection.x + offsetX,
      y: match.detection.y + offsetY,
    },
    segment: shiftedSegment,
  };
}

/**
 * One Roboflow workflow run, reduced to the detections on its chosen output.
 *
 * The workflow is named by plain values, so a caller is free to hold this
 * config under whatever schema suits it.
 *
 * Two obligations stay with the caller. The workspace, workflow and output
 * names are assumed present and are not checked here. And matches come back in
 * the coordinate space of the image passed in, so a caller that cropped to a
 * region of interest owns shifting them back into full-frame coordinates.
 */
/**
 * Runs one frame through a Roboflow workflow, on a runner or on the robot.
 *
 * A GPU task runner IS a vision-bot — a remote one. The RPC is the same either
 * way, so the only thing a runner adds is which host answers it, and a robot
 * with no runner can serve its own. That is what lets an Inspect skill run on a
 * box with no discrete GPU, more slowly.
 *
 * An EMPTY id means local vision. A configured one that is missing is an error
 * rather than a fallback, unlike the ClickFind helpers beside this: a workflow
 * is preloaded onto one particular vision-bot's inference container, so quietly
 * running it elsewhere either stalls mid-routine downloading it or fails, and
 * changing which machine judged a part is not a silent detail of an inspection
 * record.
 *
 * Health is deliberately NOT checked here. Each caller checks it once, at the
 * point where failing is cheapest — before a frame is captured, for a step that
 * would otherwise pay for one it cannot use.
 */
/**
 * Warms a Roboflow workflow on the runner, or on the robot when none is set.
 *
 * The same resolution `callRunRoboflowDetection` makes, and deliberately so: a
 * workflow preloaded onto one vision-bot and then run against another is warm
 * in the wrong place, and the first real inference pays the download the
 * preload was supposed to have covered. That is also why neither falls back to
 * local vision when a configured runner is down.
 */
export async function callPreloadRoboflowWorkflow(args: {
  equipment: EquipmentInterface;
  vision: VisionInterface;
  gpuTaskRunnerId: string;
  workspaceName: string;
  workflowId: string;
}): Promise<PreloadRoboflowWorkflowResponse> {
  const { equipment, vision, gpuTaskRunnerId, workspaceName, workflowId } =
    args;

  if (!gpuTaskRunnerId) {
    return vision.preloadRoboflowWorkflow({ workspaceName, workflowId });
  }

  const gpuTaskRunner = equipment.getDeviceById(
    gpuTaskRunnerId,
  ) as GPUTaskRunner;

  if (!gpuTaskRunner) {
    throw new Error(
      `Could not find GPU Task Runner equipment with id ${gpuTaskRunnerId}`,
    );
  }

  if (!(await gpuTaskRunner.updateAsync()).isVisionBotConnected) {
    throw new Error(
      `GPU Task Runner '${gpuTaskRunnerId}' is not healthy (vision bot disconnected)`,
    );
  }

  return (await gpuTaskRunner.executeVisionCommand({
    command: 'preload_roboflow_workflow',
    workspaceName,
    workflowId,
  })) as PreloadRoboflowWorkflowResponse;
}

export async function callRunRoboflowDetection(args: {
  equipment: EquipmentInterface;
  vision: VisionInterface;
  gpuTaskRunnerId: string;
  base64Image: string;
  workspaceName: string;
  workflowId: string;
  outputName: string;
  thresholdingParams?: Record<string, string>;
  workflowInputs?: Record<string, string>;
  preview?: boolean;
}): Promise<{ matches: LocateMatch[]; activeLearningStatus?: string }> {
  const { equipment, vision, gpuTaskRunnerId, ...rest } = args;

  if (!gpuTaskRunnerId) {
    return runRoboflowDetectionOnVision({ vision, ...rest });
  }

  const gpuTaskRunner = equipment.getDeviceById(
    gpuTaskRunnerId,
  ) as GPUTaskRunner;

  if (!gpuTaskRunner) {
    throw new Error(
      `Could not find GPU Task Runner equipment with id ${gpuTaskRunnerId}`,
    );
  }

  return runRoboflowDetection({ gpuTaskRunner, ...rest });
}

/**
 * The robot's own vision-bot, for a skill configured with no runner.
 *
 * No API key here: the vision client holds a reader and fills it in, the same
 * split the runner path makes by handing `GPUTaskRunner` one at construction.
 * A caller in the routine runner has no business holding the credential.
 */
async function runRoboflowDetectionOnVision({
  vision,
  base64Image,
  workspaceName,
  workflowId,
  outputName,
  thresholdingParams,
  workflowInputs,
  preview,
}: {
  vision: VisionInterface;
  base64Image: string;
  workspaceName: string;
  workflowId: string;
  outputName: string;
  thresholdingParams?: Record<string, string>;
  workflowInputs?: Record<string, string>;
  preview?: boolean;
}): Promise<{ matches: LocateMatch[]; activeLearningStatus?: string }> {
  const response = await vision.runRoboflowWorkflowDetection({
    workspaceName,
    workflowId,
    outputName,
    image: Buffer.from(base64Image, 'base64') as never,
    thresholdingParams: thresholdingParams ?? {},
    workflowInputs: workflowInputs ?? {},
    preview: preview ?? false,
  });

  return parseRoboflowDetectionResponse(response, {
    workspaceName,
    workflowId,
    outputName,
  });
}

export async function runRoboflowDetection({
  gpuTaskRunner,
  base64Image,
  workspaceName,
  workflowId,
  outputName,
  thresholdingParams,
  workflowInputs,
  preview,
}: {
  gpuTaskRunner: GPUTaskRunner;
  base64Image: string;
  workspaceName: string;
  workflowId: string;
  outputName: string;
  thresholdingParams?: Record<string, string>;
  workflowInputs?: Record<string, string>;
  preview?: boolean;
}): Promise<{
  matches: LocateMatch[];
  /**
   * What the workflow's active-learning output reported, when it declares one.
   *
   * Returned rather than passed out through a callback: there is no ordering
   * requirement here, so a caller that wants it can read it and a caller that
   * does not can ignore it.
   */
  activeLearningStatus?: string;
}> {
  const response = await gpuTaskRunner.executeVisionCommand({
    command: 'run_roboflow_workflow_detection',
    image: base64Image,
    workspaceName,
    workflowId,
    outputName,
    thresholding_params: thresholdingParams,
    workflow_inputs: workflowInputs ?? {},
    preview: preview ?? false,
  });

  return parseRoboflowDetectionResponse(response, {
    workspaceName,
    workflowId,
    outputName,
  });
}

/**
 * Validates a detection response and turns it into matches.
 *
 * Shared by the runner and local paths, which receive the same response object
 * over different transports. Two copies of this drifted immediately: the thing
 * worth getting right is that an unset oneof FAILS rather than reading as "found
 * nothing", and that is exactly the kind of check one copy quietly loses.
 */
function parseRoboflowDetectionResponse(
  response: unknown,
  workflow: { workspaceName: string; workflowId: string; outputName: string },
): { matches: LocateMatch[]; activeLearningStatus?: string } {
  const parsed = RunRoboflowWorkflowDetectionResponse.safeParse(response);

  if (!parsed.success) {
    log.error(
      'runRoboflowDetection',
      'Roboflow detection returned invalid response format',
      {
        response,
        parseError: parsed.error.issues,
      },
    );

    throw new Error(
      `Roboflow detection returned invalid response format: ${parsed.error.issues
        .map((i) => i.message)
        .join(', ')}`,
    );
  }

  if (parsed.data.error) {
    log.error(
      'runRoboflowDetection',
      `Roboflow detection failed: ${parsed.data.error.message}`,
      { errorCode: parsed.data.error.code },
    );

    throw new Error(`Roboflow detection failed: ${parsed.data.error.message}`);
  }

  // Both oneof arms unset means something is wrong upstream — a vision-bot
  // that predates the RPC, or a handler that returned without setting either.
  // Treating it as "found nothing" would surface later as a misleading
  // "object not found" instead of the actual RPC problem, so fail like the
  // checkpoint path does.
  if (!parsed.data.data) {
    throw new Error('Unexpected response type after validation');
  }

  const { blobs, processingTimeMs, activeLearningStatus } = parsed.data.data;

  log.info(
    'runRoboflowDetection',
    `Roboflow detection completed successfully, found ${blobs.length} blobs`,
    {
      processingTimeMs,
      // Names what actually ran. A bare `modelUrl` here read as a
      // misconfigured model run, which is not what this path does.
      workflow: `${workflow.workspaceName}/${workflow.workflowId}#${workflow.outputName}`,
      activeLearningStatus,
    },
  );

  return { matches: blobs, activeLearningStatus };
}

export async function getCustomCheckpointBlobs({
  image,
  regionOfInterest,
  method,
  equipment,
  vision,
  preview,
}: {
  image: ArrayBuffer;
  regionOfInterest: RegionOfInterest;
  method: LocateMethod;
  equipment: EquipmentInterface;
  vision: VisionInterface;
  preview?: boolean;
}): Promise<LocateMatch[]> {
  if (method.kind !== 'CustomCheckpointDetection2D') {
    throw new Error('Expected CustomCheckpointDetection2D method');
  }

  // A configured runner has to be healthy whichever branch below runs: a
  // checkpoint lives on it, and a workflow was preloaded onto it. An EMPTY id
  // is local vision, which has no separate health to check — if the robot's own
  // vision-bot is down, nothing here is running anyway.
  if (method.settings.gpuTaskRunnerId) {
    const configuredRunner = equipment.getDeviceById(
      method.settings.gpuTaskRunnerId,
    ) as GPUTaskRunner | undefined;

    if (!configuredRunner) {
      throw new Error(
        `Could not find GPU Task Runner equipment with id ${method.settings.gpuTaskRunnerId}`,
      );
    }

    if (!configuredRunner.update().isVisionBotConnected) {
      throw new Error(
        `GPU Task Runner '${method.settings.gpuTaskRunnerId}' is not healthy (vision bot disconnected).`,
      );
    }
  }

  // Preprocess the image using the vision service for ROI cropping
  const { croppedImage, offsetX, offsetY } = await vision.preprocessImage(
    image,
    regionOfInterest,
  );

  // Convert cropped image to base64
  const base64Image = Buffer.from(croppedImage).toString('base64');

  // A Roboflow source runs via a dedicated RPC (api_key injected robot-side)
  // that returns LocateMatch[] like checkpoint inference, so either flavour flows
  // through the same shift + plane-projection path below. A workflow needs one
  // extra thing a model does not: which of its outputs carries the detections.
  if (method.settings.modelSource === 'roboflow') {
    const {
      roboflowWorkflowId,
      roboflowOutputName,
      roboflowWorkspaceName,
      roboflowWorkflowInputs,
    } = method.settings;

    if (!roboflowWorkflowId) {
      throw new Error('Roboflow source selected but no workflow was chosen.');
    }

    if (!roboflowOutputName) {
      throw new Error('Roboflow workflow selected but no output was chosen.');
    }

    if (!roboflowWorkspaceName) {
      throw new Error(
        'Roboflow workflow selected but no workspace was recorded.',
      );
    }

    // `use_sam` is dropped rather than forwarded: this path never runs SAM2, so
    // sending it would have the request claim segmentation that never happens.
    // Stripped here rather than at the UI so skills saved before the switch was
    // hidden don't keep carrying it. Every other key is forwarded as
    // given: confidence_threshold, nms_threshold and max_detections are what a
    // locate skill sets, and Inspect's preview reaches vision-bot through here
    // too, carrying max_detections_per_class as well.
    const { use_sam: _unusedUseSam, ...roboflowThresholdingParams } =
      method.settings.thresholdingParams;

    // No runner required. A GPU task runner IS a vision-bot, so the robot's
    // own serves the same workflow where none is configured — which is what
    // lets a box with no discrete GPU run this, more slowly.
    const { matches: roboflowBlobs } = await callRunRoboflowDetection({
      equipment,
      vision,
      gpuTaskRunnerId: method.settings.gpuTaskRunnerId,
      base64Image,
      workspaceName: roboflowWorkspaceName,
      workflowId: roboflowWorkflowId,
      outputName: roboflowOutputName,
      thresholdingParams: roboflowThresholdingParams,
      workflowInputs: roboflowWorkflowInputs,
      preview,
    });

    return roboflowBlobs.map((match) =>
      shiftMatchByOffset(match, offsetX, offsetY),
    );
  }

  // A checkpoint model, unlike a workflow, is a FILE on a particular runner, so
  // this half genuinely needs one. Checked here rather than at the top of the
  // function, which would have refused a Roboflow workflow that needs nothing
  // of the sort.
  const gpuTaskRunner = equipment.getDeviceById(
    method.settings.gpuTaskRunnerId,
  ) as GPUTaskRunner;

  if (!gpuTaskRunner) {
    throw new Error(
      `Could not find GPU Task Runner equipment with id ${method.settings.gpuTaskRunnerId}`,
    );
  }

  if (!gpuTaskRunner.update().isVisionBotConnected) {
    throw new Error(
      `GPU Task Runner '${method.settings.gpuTaskRunnerId}' is not healthy (vision bot disconnected). Custom checkpoint detection requires a connected GPU Task Runner.`,
    );
  }

  // Execute the RunCheckpointBasedInference command
  const response = await gpuTaskRunner.executeVisionCommand({
    command: 'run_inference',
    image: base64Image,
    model_path: method.settings.modelPath,
    model_type: method.settings.modelType,
    thresholding_params: method.settings.thresholdingParams,
  });

  // Parse and validate the response using Zod schema
  const parseResult = RunCheckpointBasedInferenceResponse.safeParse(response);

  if (!parseResult.success) {
    log.error(
      'getCustomCheckpointBlobs',
      'Custom checkpoint inference returned invalid response format',
      {
        response,
        parseError: parseResult.error.issues,
      },
    );

    throw new Error(
      `Custom checkpoint inference returned invalid response format: ${parseResult.error.issues.map((i: any) => i.message).join(', ')}`,
    );
  }

  const validatedResponse = parseResult.data;

  // Handle error response
  if (validatedResponse.error) {
    log.error(
      'getCustomCheckpointBlobs',
      `Custom checkpoint inference failed: ${validatedResponse.error.message}`,
      { errorCode: validatedResponse.error.code },
    );

    throw new Error(
      `Custom checkpoint inference failed: ${validatedResponse.error.message}`,
    );
  }

  // Handle successful inference result
  if (validatedResponse.data) {
    const inferenceResult = validatedResponse.data;
    const { blobs } = inferenceResult;

    log.info(
      'getCustomCheckpointBlobs',
      `Custom checkpoint inference completed successfully, found ${blobs.length} blobs`,
      {
        processingTimeMs: inferenceResult.processingTimeMs,
        modelPath: inferenceResult.modelPath,
      },
    );

    // The proto blobs are LocateMatch (detector box + optional SAM2 segment).
    // Shift each back to original-image space: the detection box center and the
    // segment contour points both live in the cropped-ROI frame.
    return blobs.map((match) => shiftMatchByOffset(match, offsetX, offsetY));
  }

  throw new Error('Unexpected response type after validation');
}

/**
 * Text-prompt detection, bounded by the method's match-area range. Both the
 * locate pipeline and the preview runner come through here, so the filter
 * lives here rather than at either call site.
 */
export async function getTextPromptBlobs({
  areaProjection,
  ...args
}: Parameters<typeof detectTextPromptBlobs>[0] & {
  /** Set when the frame can be measured on the skill's taught plane. */
  areaProjection?: MatchAreaProjection;
}): Promise<LocateMatch[]> {
  const blobs = await detectTextPromptBlobs(args);

  const withinBounds = blobs.filter(
    makeMatchAreaFilter(args.params, areaProjection),
  );

  // A run read back from the logs should account for every detection: the
  // detector says what it found, this says what the size bounds took. Only
  // when they differ — an unbounded config drops nothing and stays quiet.
  if (withinBounds.length !== blobs.length) {
    log.info(
      'getTextPromptBlobs',
      `Match size bounds (${describeMatchAreaBounds(
        args.params,
      )}) dropped ${blobs.length - withinBounds.length} of ${
        blobs.length
      } detections`,
    );
  }

  return withinBounds;
}

async function detectTextPromptBlobs({
  image,
  regionOfInterest,
  params,
  equipment,
  vision,
}: {
  image: ArrayBuffer;
  regionOfInterest: RegionOfInterest;
  params: TextPromptDetection2DParams;
  equipment: EquipmentInterface;
  vision: VisionInterface;
}): Promise<LocateMatch[]> {
  // If no GPU Task Runner is specified, use the vision service directly
  if (!params.gpuTaskRunnerId) {
    return vision.detect2DTextPrompt(image, regionOfInterest, params);
  }

  // Get the GPUTaskRunner equipment
  const gpuTaskRunner = equipment.getDeviceById(
    params.gpuTaskRunnerId,
  ) as GPUTaskRunner;

  if (!gpuTaskRunner) {
    throw new Error(
      `Could not find GPU Task Runner equipment with id ${params.gpuTaskRunnerId}`,
    );
  }

  // Fall back to local vision if GPU's vision bot is not connected
  if (!gpuTaskRunner.update().isVisionBotConnected) {
    log.warn(
      'detectTextPromptBlobs',
      'GPU Task Runner vision bot is not connected, falling back to local vision',
    );

    return vision.detect2DTextPrompt(image, regionOfInterest, params);
  }

  // Preprocess the image using the vision service for ROI cropping
  const { croppedImage, offsetX, offsetY } = await vision.preprocessImage(
    image,
    regionOfInterest,
  );

  // Convert cropped image to base64
  const base64Image = Buffer.from(croppedImage).toString('base64');

  // Execute the text prompt detection via GPU Task Runner
  const response = await gpuTaskRunner.executeVisionCommand({
    command: 'run_inference',
    image: base64Image,
    model_path: 'text_prompt',
    model_type: 'TextPrompt',
    thresholding_params: {
      prompt: params.textInput,
      use_sam: params.useSam.toString(),
      box_threshold: params.confidenceThreshold.toString(),
      text_threshold: params.confidenceThreshold.toString(),
    },
  });

  const parseResult = RunCheckpointBasedInferenceResponse.safeParse(response);

  if (!parseResult.success) {
    log.error(
      'detectTextPromptBlobs',
      'Text prompt detection returned invalid response format',
      {
        response,
        parseError: parseResult.error.issues,
      },
    );

    throw new Error(
      `Text prompt detection returned invalid response format: ${parseResult.error.issues
        .map((issue: any) => issue.message)
        .join(', ')}`,
    );
  }

  const validatedResponse = parseResult.data;

  if (validatedResponse.error) {
    log.error(
      'detectTextPromptBlobs',
      `Text prompt detection result validation failed: ${response}. Error: ${validatedResponse.error.message}`,
      { errorCode: validatedResponse.error.code },
    );

    throw new Error(
      `Text prompt detection result validation failed: ${validatedResponse.error.message}`,
    );
  }

  if (validatedResponse.data) {
    const { blobs } = validatedResponse.data;

    log.info(
      'detectTextPromptBlobs',
      `Text prompt detection completed successfully, found ${blobs.length} blobs`,
      {
        processingTimeMs: validatedResponse.data.processingTimeMs,
      },
    );

    // Adjust the detection box center and segment contour back to original
    // image space (both are in cropped-ROI coords from the GPU detector).
    return blobs.map((match) => shiftMatchByOffset(match, offsetX, offsetY));
  }

  throw new Error('Unexpected response type after validation');
}

// Serialize the step-config head (zod schema, see LocateTypes) into the proto
// oneof shape the ComputeDinoEmbeddings RPC expects. The zod `kind`/`version`
// fields are schema-level discriminators with no proto counterpart.
export function toLocateHeadProto(head: DinoFastLocateHead): LocateHeadProto {
  if (head.kind === 'mlp') {
    return {
      mlp: {
        inputDim: head.inputDim,
        hidden: head.hidden,
        w1: head.w1,
        b1: head.b1,
        w2: head.w2,
        b2: head.b2,
      },
    };
  }

  return {
    logistic: { inputDim: head.inputDim, w: head.w, b: head.b },
  };
}

export async function callComputeDinoEmbeddings({
  image,
  modelVersion,
  precision,
  locateHead,
  omitTensor = false,
  equipment,
  vision,
  gpuTaskRunnerId,
}: {
  image: ArrayBuffer;
  modelVersion: string;
  precision: string;
  // When set, vision-bot forward-scores the patch grid with this head and
  // returns `heatmapFp16`; pair it with `omitTensor: true` to skip the ~1MB
  // tensor payload. Plain embedding calls leave both unset and keep the
  // patch tensor.
  locateHead?: LocateHeadProto;
  omitTensor?: boolean;
  equipment: EquipmentInterface;
  vision: VisionInterface;
  gpuTaskRunnerId: string;
}): Promise<ComputeDinoEmbeddingsResponse> {
  const request = {
    image: new Uint8Array(image),
    modelVersion,
    precision,
    locateHead,
    omitTensor,
  };

  if (!gpuTaskRunnerId) {
    return vision.computeDinoEmbeddings(request);
  }

  const gpuTaskRunner = equipment.getDeviceById(
    gpuTaskRunnerId,
  ) as GPUTaskRunner;

  if (!gpuTaskRunner) {
    throw new Error(
      `Could not find GPU Task Runner equipment with id ${gpuTaskRunnerId}`,
    );
  }

  if (!gpuTaskRunner.update().isVisionBotConnected) {
    log.warn(
      'callComputeDinoEmbeddings',
      'GPU Task Runner vision bot is not connected, falling back to local vision',
    );

    return vision.computeDinoEmbeddings(request);
  }

  return gpuTaskRunner.computeDinoEmbeddings(request);
}

export async function callComputeDinoImageEmbedding({
  image,
  modelVersion,
  precision,
  equipment,
  vision,
  gpuTaskRunnerId,
}: {
  image: ArrayBuffer;
  modelVersion: string;
  precision: string;
  equipment: EquipmentInterface;
  vision: VisionInterface;
  gpuTaskRunnerId: string;
}): Promise<ComputeDinoImageEmbeddingResponse> {
  const request = {
    image: new Uint8Array(image),
    modelVersion,
    precision,
  };

  if (!gpuTaskRunnerId) {
    return vision.computeDinoImageEmbedding(request);
  }

  const gpuTaskRunner = equipment.getDeviceById(
    gpuTaskRunnerId,
  ) as GPUTaskRunner;

  if (!gpuTaskRunner) {
    throw new Error(
      `Could not find GPU Task Runner equipment with id ${gpuTaskRunnerId}`,
    );
  }

  if (!gpuTaskRunner.update().isVisionBotConnected) {
    log.warn(
      'callComputeDinoImageEmbedding',
      'GPU Task Runner vision bot is not connected, falling back to local vision',
    );

    return vision.computeDinoImageEmbedding(request);
  }

  return gpuTaskRunner.computeDinoImageEmbedding(request);
}

// Mirrors callComputeDinoEmbeddings: routes through the GPU Task Runner when
// configured + healthy, falls back to the local vision service otherwise.
export async function callTrainLocateHead({
  image,
  sessionId,
  seq,
  clicks,
  committedPositives,
  committedNegatives,
  headKind,
  modelVersion,
  precision,
  equipment,
  vision,
  gpuTaskRunnerId,
}: {
  image: ArrayBuffer;
  sessionId: string;
  seq: number;
  clicks: Array<{ nx: number; ny: number; positive: boolean }>;
  committedPositives: Array<{ values: number[] }>;
  committedNegatives: Array<{ values: number[] }>;
  headKind: 'mlp' | 'logistic';
  modelVersion: string;
  precision: string;
  equipment: EquipmentInterface;
  vision: VisionInterface;
  gpuTaskRunnerId: string;
}): Promise<TrainLocateHeadResponse> {
  const request = {
    sessionId,
    seq,
    image: new Uint8Array(image),
    modelVersion,
    precision,
    clicks,
    committedPositives,
    committedNegatives,
    headKind:
      headKind === 'mlp'
        ? LocateHeadKind.LOCATE_HEAD_KIND_MLP
        : LocateHeadKind.LOCATE_HEAD_KIND_LOGISTIC,
  };

  if (!gpuTaskRunnerId) {
    return vision.trainLocateHead(request);
  }

  const gpuTaskRunner = equipment.getDeviceById(
    gpuTaskRunnerId,
  ) as GPUTaskRunner;

  if (!gpuTaskRunner) {
    throw new Error(
      `Could not find GPU Task Runner equipment with id ${gpuTaskRunnerId}`,
    );
  }

  if (!gpuTaskRunner.update().isVisionBotConnected) {
    log.warn(
      'callTrainLocateHead',
      'GPU Task Runner vision bot is not connected, falling back to local vision',
    );

    return vision.trainLocateHead(request);
  }

  return gpuTaskRunner.trainLocateHead(request);
}

// Mirrors callComputeDinoEmbeddings: routes through the GPU Task Runner when
// configured + healthy, falls back to the local vision service otherwise.
export async function callSegmentMask({
  image,
  boxes,
  method,
  heatmapSamples,
  heatmapThreshold,
  heatmapDense,
  equipment,
  vision,
  gpuTaskRunnerId,
}: {
  image: ArrayBuffer;
  // `points` is optional: the DinoFast live-detection path supplies heatmap
  // point prompts, while the teach-modal path sends box-only prompts.
  boxes: {
    x1: number;
    y1: number;
    x2: number;
    y2: number;
    points?: { x: number; y: number; positive: boolean }[];
  }[];
  // Segmentation backend: 'sam2' (default) or 'bilateral_solver'. Defaults to
  // 'sam2' so existing callers compile and behave unchanged without passing
  // this arg.
  method?: 'sam2' | 'bilateral_solver' | 'heatmap_threshold';
  // Optional dense heatmap samples (cropped-ROI pixel space) + the object/
  // background threshold. When supplied, vision-bot runs SAM2 multimask and
  // picks the heatmap-aligned candidate per box; omitted ⇒ single-mask.
  heatmapSamples?: { x: number; y: number; score: number }[];
  heatmapThreshold?: number;
  // Dense fp16 heatmap. The bilateral_solver method consumes it as its input;
  // SAM2 uses it (when present) to gate masks back to the heatmap's foreground
  // support.
  heatmapDense?: { data: Uint8Array; width: number; height: number };
  equipment: EquipmentInterface;
  vision: VisionInterface;
  gpuTaskRunnerId: string;
}): Promise<SegmentMaskResponse> {
  // Normalize to the generated request shape (Box2DXyxy.points is required);
  // a missing `points` becomes an empty list = box-only prompt.
  const methodEnum = {
    sam2: 0,
    bilateral_solver: 2,
    heatmap_threshold: 3,
  }[method ?? 'sam2'];

  const request = {
    image: new Uint8Array(image),
    boxes: boxes.map((b) => ({ ...b, points: b.points ?? [] })),
    method: methodEnum,
    heatmapSamples: heatmapSamples ?? [],
    heatmapThreshold: heatmapThreshold ?? 0,
    heatmapDense: heatmapDense?.data ?? new Uint8Array(0),
    heatmapWidth: heatmapDense?.width ?? 0,
    heatmapHeight: heatmapDense?.height ?? 0,
  };

  if (!gpuTaskRunnerId) {
    return vision.segmentMask(request);
  }

  const gpuTaskRunner = equipment.getDeviceById(
    gpuTaskRunnerId,
  ) as GPUTaskRunner;

  if (!gpuTaskRunner) {
    throw new Error(
      `Could not find GPU Task Runner equipment with id ${gpuTaskRunnerId}`,
    );
  }

  if (!gpuTaskRunner.update().isVisionBotConnected) {
    log.warn(
      'callSegmentMask',
      'GPU Task Runner vision bot is not connected, falling back to local vision',
    );

    return vision.segmentMask(request);
  }

  return gpuTaskRunner.segmentMask(request);
}

// Teach-time: establish the canonical orientation reference from a taught
// contour. Forwards the contour to vision-bot's ComputeOrientationReference
// RPC and maps the response oneof back to a plain { isReliable, reference
// contour } shape. On the error branch (or an unreliable shape) the
// referenceContour is empty and the frontend persists nothing.
// vision-bot's enum ordinals, mapped to the string form the wizard persists and
// the UI switches on. UNSPECIFIED (and anything unrecognized) becomes null —
// callers treat that as "no reference, reason unknown".
const ORIENTATION_REJECTION_BY_ORDINAL: Record<
  number,
  OrientationReferenceRejection
> = {
  1: 'symmetric',
  2: 'sceneInconsistent',
  3: 'pcaSufficient',
};

// vision-bot's OrientationSource ordinals. UNSPECIFIED (0), an absent field,
// and anything unrecognized all become undefined — "no source reported",
// which the UI renders as nothing rather than guessing.
const ORIENTATION_SOURCE_BY_ORDINAL: Record<number, OrientationSource> = {
  1: 'template',
  2: 'pcaAxes',
  3: 'wrist',
};

export async function callComputeOrientationReference({
  contour,
  siblingContours,
  vision,
}: {
  contour: { x: number; y: number }[];
  // The teach scene's other matches. vision-bot registers each against the
  // primary and withholds the reference unless they all agree.
  siblingContours?: { x: number; y: number }[][];
  vision: VisionInterface;
}): Promise<{
  isReliable: boolean;
  referenceContour: number[][];
  rejection: OrientationReferenceRejection | null;
  // Teach-scene registration counts, echoed back on both branches below. See
  // RunComputeOrientationReferenceResult for what these mean.
  registeredSiblings?: number;
  checkedSiblings?: number;
}> {
  const response = await vision.computeOrientationReference({
    contour: contour.map((p) => ({ x: p.x, y: p.y })),
    siblingContours: (siblingContours ?? []).map((sibling) => ({
      points: sibling.map((p) => ({ x: p.x, y: p.y })),
    })),
  });

  if (response.error) {
    throw new Error(
      `ComputeOrientationReference failed: ${response.error.message}`,
    );
  }

  const { data } = response;

  if (!data || !data.isReliable || !data.reference) {
    return {
      isReliable: false,
      referenceContour: [],
      rejection: ORIENTATION_REJECTION_BY_ORDINAL[data?.rejection ?? 0] ?? null,
      registeredSiblings: data?.registeredSiblings,
      checkedSiblings: data?.checkedSiblings,
    };
  }

  return {
    isReliable: true,
    referenceContour: data.reference.referenceContour.map((p) => [p.x, p.y]),
    rejection: null,
    registeredSiblings: data.registeredSiblings,
    checkedSiblings: data.checkedSiblings,
  };
}

// Convert an oriented detection box (center + width/height + rotation) to the
// tightest axis-aligned xyxy bbox that contains its rotated corners. SAM2's
// box prompts are axis-aligned only — feeding it the rotated rect directly
// would either crash or be silently misinterpreted as xyxy. The box is the
// pre-segmentation detector result (Detection2D).
function obbToAxisAlignedXyxy(detection: Detection2D): {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
} {
  // No measured rotation means the box is already axis-aligned, so its xyxy is
  // the box itself — which is what a 0 here produces.
  const rotation = detection.rotation ?? 0;
  const cos = Math.cos(rotation);
  const sin = Math.sin(rotation);
  const halfW = detection.width / 2;
  const halfH = detection.height / 2;

  const corners = [
    [-halfW, -halfH],
    [halfW, -halfH],
    [halfW, halfH],
    [-halfW, halfH],
  ].map(([dx, dy]) => ({
    x: detection.x + dx * cos - dy * sin,
    y: detection.y + dx * sin + dy * cos,
  }));

  const xs = corners.map((p) => p.x);
  const ys = corners.map((p) => p.y);

  return {
    x1: Math.min(...xs),
    y1: Math.min(...ys),
    x2: Math.max(...xs),
    y2: Math.max(...ys),
  };
}

// Parse the source dimensions out of a JPEG's Start-Of-Frame marker.
// Returns `undefined` if the input isn't a recognizable baseline JPEG — the
// caller falls back to the encoder's letterbox content dims in that case.
//
// JPEG layout: starts with SOI (0xFFD8), then a sequence of segments each
// beginning with 0xFF<marker>. SOFn markers (0xC0..0xCF, except 0xC4/C8/CC)
// carry: length(2), precision(1), height(2), width(2). We don't need to
// fully decode — just walk segments until we hit any SOFn.
function getJpegSize(
  image: ArrayBuffer,
): { width: number; height: number } | undefined {
  if (image.byteLength < 4) return undefined;

  const view = new DataView(image);

  if (view.getUint16(0) !== 0xffd8) return undefined;

  let i = 2;

  while (i + 1 < view.byteLength) {
    if (view.getUint8(i) !== 0xff) return undefined;

    let marker = view.getUint8(i + 1);

    // 0xFF padding bytes between markers.
    while (marker === 0xff && i + 2 < view.byteLength) {
      i += 1;
      marker = view.getUint8(i + 1);
    }

    i += 2;

    // SOFn (baseline / progressive / lossless / etc.) — carry width+height.
    if (
      marker >= 0xc0 &&
      marker <= 0xcf &&
      marker !== 0xc4 && // DHT
      marker !== 0xc8 && // JPG (reserved)
      marker !== 0xcc // DAC
    ) {
      if (i + 7 > view.byteLength) return undefined;

      return {
        height: view.getUint16(i + 3),
        width: view.getUint16(i + 5),
      };
    }

    // Standalone markers with no length payload: SOI/EOI/RSTn.
    if (
      marker === 0xd8 ||
      marker === 0xd9 ||
      (marker >= 0xd0 && marker <= 0xd7)
    ) {
      continue; // eslint-disable-line no-continue
    }

    if (i + 2 > view.byteLength) return undefined;

    const segmentLength = view.getUint16(i);

    if (segmentLength < 2) return undefined;

    i += segmentLength;
  }

  return undefined;
}

export interface DinoFastDetections {
  matches: LocateMatch[];
  // The dense score map the matches were thresholded from; populated only
  // when `returnHeatmap` was requested (teach-preview overlay), null
  // otherwise.
  heatmap: DinoFastDenseHeatmap | null;
}

export async function getDinoFastBlobs({
  image,
  regionOfInterest,
  params,
  equipment,
  vision,
  returnHeatmap = false,
  areaProjection,
}: {
  image: ArrayBuffer;
  regionOfInterest?: RegionOfInterest;
  params: DinoFastParams;
  equipment: EquipmentInterface;
  vision: VisionInterface;
  returnHeatmap?: boolean;
  /** Set when the frame can be measured on the skill's plane, for cm² bounds. */
  areaProjection?: MatchAreaProjection;
}): Promise<DinoFastDetections> {
  // Apply the user's ROI before encoding so embeddings cover the cropped
  // region only — matches getTextPromptBlobs / getCustomCheckpointBlobs.
  // `vision.preprocessImage` handles the "ROI = full image" case as a no-op.
  let croppedImage = image;
  let offsetX = 0;
  let offsetY = 0;

  if (regionOfInterest) {
    const preprocessed = await vision.preprocessImage(image, regionOfInterest);
    croppedImage = preprocessed.croppedImage;
    offsetX = preprocessed.offsetX;
    offsetY = preprocessed.offsetY;
  }

  // Read the source-image dims directly from the JPEG header so we can map
  // patch coords back to the actual frame's pixel space. The encoder rescales
  // the source down to its square `inputW × inputH` letterbox, so deriving
  // dims from `inputW - 2 * paddingLeft` would silently return them in the
  // *scaled* space — bboxes would then render in the wrong area of the live
  // video stream (which has its own native resolution).
  const sourceSize = getJpegSize(croppedImage);

  // A learned head is forward-scored by vision-bot itself (the response
  // carries the fp16 heatmap), so the raw patch tensor is only downloaded for
  // the client-side cosine scorer.
  const tensorResp = await callComputeDinoEmbeddings({
    image: croppedImage,
    modelVersion: params.embeddingModelVersion,
    precision: params.precision,
    locateHead: params.locateHead
      ? toLocateHeadProto(params.locateHead)
      : undefined,
    omitTensor: params.locateHead != null,
    equipment,
    vision,
    gpuTaskRunnerId: params.gpuTaskRunnerId,
  });

  if (tensorResp.error) {
    log.error('runLocate.dinoFast', 'encoder failed', {
      detail: tensorResp.error.message,
    });

    throw new Error(CLICKFIND_ERROR_MESSAGES.ENCODER_FAILED);
  }

  if (!tensorResp.data) {
    log.error('runLocate.dinoFast', 'encoder returned no data');
    throw new Error(CLICKFIND_ERROR_MESSAGES.ENCODER_FAILED);
  }

  const { data } = tensorResp;

  // A placeholder `embeddingModelVersion` (empty default or legacy seed) was
  // never pinned to a real encoder capture, so it can't signal a genuine
  // mismatch — the encoder is authoritative and the references being matched
  // were embedded by whatever encoder is live now. Only a version that was
  // actually adopted from a prior encoder response indicates stale embeddings.
  if (
    !isUnpinnedModelVersion(params.embeddingModelVersion) &&
    data.modelVersion !== params.embeddingModelVersion
  ) {
    log.error('runLocate.dinoFast', 'encoder model mismatch', {
      saved: params.embeddingModelVersion,
      running: data.modelVersion,
    });

    throw new Error(CLICKFIND_ERROR_MESSAGES.ENCODER_MISMATCH);
  }

  // Learned head (MLP or logistic) replaces cosine scoring when present
  // (trained in the teach UI and serialized into the step config): vision-bot
  // forward-scores the grid server-side and the response omits the raw tensor
  // entirely, so it must not be reconstructed here — only the fp16 heatmap is
  // decoded. Legacy/untaught steps have no head and keep the original
  // client-side cosine scorer unchanged.
  let heatmap: Float32Array;

  if (params.locateHead) {
    if (!data.heatmapFp16) {
      log.error(
        'runLocate.dinoFast',
        'encoder returned no heatmap for a learned-head request',
      );

      throw new Error(CLICKFIND_ERROR_MESSAGES.ENCODER_FAILED);
    }

    const fp16 = uint8BytesToUint16(data.heatmapFp16);

    if (fp16.length !== data.gridH * data.gridW) {
      log.error('runLocate.dinoFast', 'heatmap size mismatch', {
        expected: data.gridH * data.gridW,
        got: fp16.length,
        gridH: data.gridH,
        gridW: data.gridW,
      });

      throw new Error(CLICKFIND_ERROR_MESSAGES.ENCODER_FAILED);
    }

    heatmap = new Float32Array(fp16.length);

    for (let i = 0; i < fp16.length; i += 1) {
      heatmap[i] = fp16ToFp32(fp16[i]);
    }
  } else {
    // The cosine scorer needs positive references; a learned head carries its
    // own weights and scores from the embeddings alone, so it doesn't.
    if (params.positives.length === 0) {
      log.error('runLocate.dinoFast', 'no positive references configured');
      throw new Error(CLICKFIND_ERROR_MESSAGES.NO_REFERENCES);
    }

    const tensor = PatchTensor.fromTensorBytes(
      data.gridH,
      data.gridW,
      data.embeddingDim,
      data.values,
    );

    heatmap = cosineSimilarityHeatmap({
      tensor,
      positives: params.positives,
      negatives: params.negatives,
      negativeWeight: DEFAULT_NEGATIVE_WEIGHT,
    });
  }

  // DinoFast always fits oriented bboxes to each cluster's principal axes
  // (PCA over patch coords). The old `disableRotation` axis-aligned mode was
  // removed — the SAM2 contour is the source of truth for the final shape, and
  // the detector box only needs to bound the cluster well enough to prompt it.
  const fitOriented = true;

  // Original-image dims: prefer the JPEG header (reflects the actual source
  // resolution, e.g. 1280×720) so bboxes land on the right part of the live
  // video preview. Fall back to the letterbox content dims if the header
  // couldn't be parsed (e.g. non-JPEG or in unit tests where the buffer is
  // synthetic) — those tests use a square `inputW = inputH` fixture, so the
  // fallback agrees with the source aspect there.
  const originalW = sourceSize?.width ?? data.inputW - 2 * data.paddingLeft;
  const originalH = sourceSize?.height ?? data.inputH - 2 * data.paddingTop;

  // Teach-preview overlay: ship the score map the detections below are
  // thresholded from. The learned-head path passes vision-bot's fp16 bytes
  // through untouched; the cosine path packs its locally computed fp32
  // scores. `crop` places the scored (ROI-cropped) region within the source
  // frame — offsetX/Y are 0 and the dims equal the frame when no crop
  // applied.
  const denseHeatmap: DinoFastDenseHeatmap | null = returnHeatmap
    ? {
        valuesFp16Base64: bytesToBase64(
          params.locateHead && data.heatmapFp16
            ? data.heatmapFp16
            : fp32ArrayToFp16Bytes(heatmap),
        ),
        gridH: data.gridH,
        gridW: data.gridW,
        inputW: data.inputW,
        inputH: data.inputH,
        paddingTop: data.paddingTop,
        paddingLeft: data.paddingLeft,
        modelVersion: data.modelVersion,
        crop: {
          x: offsetX,
          y: offsetY,
          width: originalW,
          height: originalH,
        },
      }
    : null;

  const rawBlobs = thresholdAndConnectedComponents({
    heatmap,
    gridH: data.gridH,
    gridW: data.gridW,
    threshold: params.scoreThreshold,
    // PCA over patch coords needs the indices preserved through CC.
    collectPatches: fitOriented,
  });

  const nmsBlobs = nonMaxSuppression(rawBlobs, params.nmsIouThreshold);

  const unletterboxCtx = {
    gridH: data.gridH,
    gridW: data.gridW,
    inputH: data.inputH,
    inputW: data.inputW,
    paddingTop: data.paddingTop,
    paddingLeft: data.paddingLeft,
    originalW,
    originalH,
  };

  // Match-area enforcement happens on the final shape — the contour when
  // segmentation produced one, else the detector OBB box — in cm² on the taught
  // plane. Without one (the depth projections filter after projection) this
  // keeps everything. (Patch count was a coarse proxy for thresholded heatmap
  // support, not true area.)
  const matchWithinBounds = makeMatchAreaFilter(params, areaProjection);

  // Segmentation off ⇒ the OBB box is the final shape, so filter detections by
  // box area before the maxBlobs cap (the cap then counts only valid matches).
  // Segmentation on ⇒ the box overestimates a small object's true extent, so we
  // do NOT pre-gate here; the contour filter at finalization is authoritative.
  // Applied once the box is in source-frame pixels, which the cm² measure
  // casts through the camera.
  const segmentationOff = !params.enableSegmentationMask;

  const detectionBoxWithinBounds = (detection: Detection2D): boolean =>
    !segmentationOff || matchWithinBounds({ detection, segment: null });

  // The heatmap-fit OBBs are the detector boxes (Detection2D). When
  // segmentation is enabled they prompt SAM2; the resulting contour becomes the
  // match's segment. When it is off, each detection becomes a segment-less
  // LocateMatch.
  let detections: Detection2D[];

  if (fitOriented) {
    const oriented = fitOrientedBlobs({
      blobs: nmsBlobs,
      gridW: data.gridW,
    });

    detections = oriented
      .map((b) => unletterboxOrientedBlob(b, unletterboxCtx))
      .map((b) => ({
        // unletterboxOrientedBlob already returns center coords (box-center
        // convention); add the ROI offset.
        x: b.cx + offsetX,
        y: b.cy + offsetY,
        width: b.width,
        height: b.height,
        score: b.score,
        rotation: b.rotation,
      }))
      .filter(detectionBoxWithinBounds)
      .sort((a, b) => b.score - a.score)
      .slice(0, params.maxBlobs);
  } else {
    detections = nmsBlobs
      .map((b) => unletterboxBlob(b, unletterboxCtx))
      .map((b) => ({
        // unletterboxBlob returns top-left coords; the Detection2D x/y
        // convention is the bbox *center* (consumers like makeRectMaskRle and
        // the Blobs2DOverlay SVG both compute `x - width/2` to get the
        // top-left). Without this offset DinoFast detections render half-a-bbox
        // up-and-left of where the encoder actually saw the cluster, and
        // downstream pose estimation projects from the wrong pixel.
        x: b.x + offsetX + b.width / 2,
        y: b.y + offsetY + b.height / 2,
        width: b.width,
        height: b.height,
        score: b.score,
        rotation: 0,
      }))
      .filter(detectionBoxWithinBounds)
      .sort((a, b) => b.score - a.score)
      .slice(0, params.maxBlobs);
  }

  // When segmentation is enabled, refine each DinoFast OBB with a SAM2 pass.
  // (Disabled ⇒ return the heatmap-patch bboxes as-is.) This is gated on the
  // explicit `enableSegmentationMask` toggle, independent of `precision` (which
  // only sets the DINO encoder grid resolution). vision-bot runs a lightweight
  // SAM2 variant — tiny on CPU, base-plus on GPU — which returns a contour-only
  // segment per detection (the detector OBB stays the box); the RLE mask rides
  // on the segment when available. We preserve the DinoFast similarity score on
  // the segment (SAM2 doesn't produce one).
  //
  // Send the cropped image (not the full frame) so SAM2's ViT encoder runs
  // on the smaller ROI pixels instead of re-encoding the whole frame. Adjust
  // box coords to cropped-image space on the way in, and add the ROI offset
  // back to blob centers + contour points on the way out.
  // No segmentation (or nothing detected): seg-off detections were already
  // box-area filtered before the maxBlobs cap; nothing more to do here.
  if (!params.enableSegmentationMask || detections.length === 0) {
    return {
      matches: detections.map((detection) => ({ detection, segment: null })),
      heatmap: denseHeatmap,
    };
  }

  // Clamp SAM2 box prompts to the cropped image bounds. The OBB envelope
  // can extend past the ROI when a detection sits right on the edge;
  // SAM2's set_image runs on cropped-image pixels only, so negative or
  // past-edge coords either crash or produce partial masks.
  const croppedW = originalW;
  const croppedH = originalH;

  const boxes = detections.map((b) => {
    const { x1, y1, x2, y2 } = obbToAxisAlignedXyxy(b);

    // Cropped-image (ROI) space — the space SAM2 and the patch grid share.
    // unletterboxCtx.originalW/H == croppedW/H, so derived point coords land
    // in this same space and need no further offset.
    const cropBox = {
      x1: Math.max(0, x1 - offsetX),
      y1: Math.max(0, y1 - offsetY),
      x2: Math.min(croppedW, x2 - offsetX),
      y2: Math.min(croppedH, y2 - offsetY),
    };

    // Positive points on the matched object + negative points on holes /
    // in-box background, so SAM2 carves out interior negative space instead
    // of filling the box. Empty when the blob is too sparse (box-only).
    const points = derivePointPrompts({
      heatmap,
      gridH: data.gridH,
      gridW: data.gridW,
      threshold: params.scoreThreshold,
      box: cropBox,
      ctx: unletterboxCtx,
      marginFraction: SAM2_SHADOW_MARGIN_FRACTION,
    });

    return { ...cropBox, points };
  });

  // Dense heatmap samples over the box regions let vision-bot run SAM2 in
  // multimask mode and pick, per box, the candidate whose interior best
  // matches the DINO heatmap — rejecting the over-inclusive mask that grabs
  // a cast shadow. Same cropped-ROI pixel space as the boxes/points above.
  const heatmapSamples = deriveHeatmapSamples({
    heatmap,
    gridH: data.gridH,
    gridW: data.gridW,
    ctx: unletterboxCtx,
    boxes,
    marginFraction: SAM2_SHADOW_MARGIN_FRACTION,
  });

  const useFilter = params.segmentationMethod !== 'sam2';

  // Dense fp16 heatmap, sent for every method: the filter methods consume it
  // as their input, and SAM2 uses it to gate its masks back to the heatmap's
  // foreground support (excising the cast-shadow bleed its box prompt pulls in
  // when there's no real image edge to stop it).
  const heatmapDense = rasterizeHeatmapFp16({
    heatmap,
    gridH: data.gridH,
    gridW: data.gridW,
    ctx: unletterboxCtx,
  });

  let segResp: SegmentMaskResponse;

  try {
    segResp = await callSegmentMask({
      image: croppedImage,
      boxes,
      method: params.segmentationMethod,
      heatmapSamples: useFilter ? undefined : heatmapSamples,
      heatmapDense,
      heatmapThreshold: params.scoreThreshold,
      equipment,
      vision,
      gpuTaskRunnerId: params.gpuTaskRunnerId,
    });
  } catch (e) {
    log.warn(
      'getDinoFastBlobs.sam2',
      'SAM2 RPC failed; returning DinoFast OBBs unrefined',
      { error: e instanceof Error ? e.message : String(e) },
    );

    return {
      matches: detections
        .map((detection) => ({ detection, segment: null }))
        .filter(matchWithinBounds),
      heatmap: denseHeatmap,
    };
  }

  if (segResp.error || !segResp.data) {
    log.warn(
      'getDinoFastBlobs.sam2',
      'SAM2 segmentation returned an error; returning DinoFast OBBs unrefined',
      { error: segResp.error },
    );

    return {
      matches: detections
        .map((detection) => ({ detection, segment: null }))
        .filter(matchWithinBounds),
      heatmap: denseHeatmap,
    };
  }

  const { blobs: segBlobs, wasSegmented } = segResp.data;

  // Pair each detector box with its SAM2 segment. The detection box stays the
  // DinoFast OBB (the source-of-truth box); the segment carries the SAM2
  // contour (source of truth for the mask). Boxes whose SAM2 pass produced no
  // mask keep `segment: null`.
  const matches = detections.map((detection, i) => {
    const segBlob = segBlobs[i];

    if (!segBlob || !wasSegmented[i]) {
      return { detection, segment: null };
    }

    const sourceFrameContour = segBlob.contour.map((p) => ({
      x: p.x + offsetX,
      y: p.y + offsetY,
    }));

    const segment: Blob2D = {
      // Preserve the DinoFast cosine-similarity score — SAM2 emits 0.
      score: detection.score,
      contour: sourceFrameContour,
      // Intentionally drop SAM2's maskRle/maskWidth/maskHeight: they're in
      // cropped-image pixel space (SAM2 ran on the ROI crop), but downstream
      // consumers — e.g. 3D pose estimation via `makeRectMaskRle` — expect
      // the mask to be aligned with the source frame and sized to the full
      // image. Re-encoding the mask into source-frame coords just to satisfy
      // those consumers would be costly; the rectangular bbox fallback at
      // the pose-estimation step rebuilds a correctly framed mask from the
      // detection box instead.
    };

    return { detection, segment };
  });

  return { matches: matches.filter(matchWithinBounds), heatmap: denseHeatmap };
}

/**
 * Read the wrist pose to pair with a frame that is about to be requested.
 *
 * Must be called *before* the first frame request, not after: the camera
 * getters return the latest frame the camera already holds, so the frame is
 * older than the call that fetches it, and the pre-request pose is the closest
 * match to its capture time. Reading it afterwards trails the fetch — and, on
 * the 3D paths, the whole detection round trip — which on a moving arm is the
 * difference between the pose at capture and the pose several hundred
 * milliseconds later.
 *
 * Not exported, and `captureDepthWithPose` is why: a caller that fetches the
 * depth frame itself has to sample before that fetch, and an exported helper
 * makes that a convention four call sites have to remember rather than
 * something the code enforces. Fetching through `captureDepthWithPose` leaves
 * no order to get wrong.
 *
 * This is the closest pose this API can pick, not the pose at capture. The
 * frame was exposed one camera pipeline latency before the request that
 * fetches it — roughly 20 ms on GigE and 135 ms on Insta360, plus up to one
 * frame period — so a residual of that gap times the arm's speed remains,
 * about 21 mm at 0.3 m/s over 70 ms. It is exact only while the arm is still.
 * Closing it needs the frame's own capture stamp (camera-bot sends one, and
 * `Camera.ts` drops it) paired against a short history of stamped poses
 * (`libs/ros/PoseSubscription.ts` drops `PoseStamped.header.stamp`); both are
 * a layer below this helper, and neither is plumbed through yet.
 *
 * Mirrors the same rule in VisionMethodRunner's detectScenePlane flow.
 */
function sampleWristPoseForFrame(
  getState: RunLocateArgs['getState'],
): CartesianPose | undefined {
  return getState()?.kinematicState?.wristPose;
}

/**
 * Fetch a depth frame together with the arm pose that matches it.
 *
 * The pairing is the whole point. `depthImage` is the geometry `robotPose`
 * lifts into the base frame, so the pose has to be read before the fetch —
 * and returning both from one call is what makes that unconditional, rather
 * than a rule each caller has to follow with nothing checking that it did.
 * Passing the depth image without its pose is not expressible.
 *
 * Callers that fetch no depth frame should pass no pose either: `runLocate`
 * samples its own immediately before the colour-frame request, which is a
 * closer match than anything the caller could read earlier.
 */
export async function captureDepthWithPose(
  equipment: NonNullable<RunLocateArgs['camera']['equipment']>,
  getState: RunLocateArgs['getState'],
): Promise<{
  depthImage: ArrayBuffer;
  wristPoseAtCapture: CartesianPose | undefined;
}> {
  const wristPoseAtCapture = sampleWristPoseForFrame(getState);

  return { depthImage: await equipment.getDepthFrame(), wristPoseAtCapture };
}

/**
 * The pose to pair with this call's frames: the caller's if it sampled one
 * before its own frame fetches, otherwise ours.
 */
function resolveWristPoseAtCapture(
  args: Pick<RunLocateArgs, 'getState' | 'wristPoseAtCapture'>,
): CartesianPose | undefined {
  return args.wristPoseAtCapture ?? sampleWristPoseForFrame(args.getState);
}

/**
 * TCP extrinsics is what makes the arm pose part of the request, so a missing
 * pose there cannot produce a correct projection. Fail here rather than
 * shipping `robotPose: undefined` to vision-bot, which rejects it only after a
 * full frame upload and surfaces as an opaque vision failure. Mirrors
 * VisionMethodRunner's guard.
 */
function requireRobotPoseForExtrinsics(
  extrinsicsKind: ExtrinsicsKinematics['kind'],
  wristPoseAtCapture: CartesianPose | undefined,
): CartesianPose | undefined {
  if (extrinsicsKind !== 'tcp') {
    return undefined;
  }

  if (!wristPoseAtCapture) {
    throw new Error('Robot pose unavailable for TCP-mounted camera');
  }

  return wristPoseAtCapture;
}

/** The match-area bounds of a method that offers them and can drop anything. */
function activeMatchAreaBounds(
  method: LocateMethod,
): MatchAreaBounds | undefined {
  if (method.kind !== 'DinoFast' && method.kind !== 'TextPromptDetection2D') {
    return undefined;
  }

  return hasMatchAreaBounds(method.settings) ? method.settings : undefined;
}

/**
 * The projection a ManualPlane match-size filter measures through, on the
 * taught plane raised by the object height. Undefined wherever there are no
 * bounds to apply or no taught plane: the depth projections are filtered after
 * projection instead (`filterProjectedMatchesByArea`), and everything else has
 * no surface to measure on, so keeps every match.
 */
async function resolveMatchAreaProjection(
  args: RunLocateArgs,
  wristPoseAtCapture: CartesianPose | undefined,
): Promise<MatchAreaProjection | undefined> {
  const { method, plane, camera } = args;

  if (
    !activeMatchAreaBounds(method) ||
    (args.projectionMethod ?? 'ManualPlane') !== 'ManualPlane' ||
    !plane ||
    !camera.equipment
  ) {
    return undefined;
  }

  const cameraPose = cameraPoseAtCapture(
    await resolveVisionExtrinsics(camera.equipment, args.extrinsics),
    wristPoseAtCapture,
  );

  if (!cameraPose) {
    log.warn(
      'resolveMatchAreaProjection',
      'No wrist pose for the frame; the match size bounds are not applied',
    );

    return undefined;
  }

  return {
    plane: raisedPlane(plane, args.objectHeight),
    cameraPose,
    intrinsics: await getIntrinsics(args.intrinsics, camera.equipment),
  };
}

/**
 * Depth-projected matches kept by their size bounds, each measured on the
 * surface its own pose sits on. Without the camera pose nothing can be
 * measured, so every match is kept.
 */
function filterProjectedMatchesByArea<
  T extends {
    blob: Parameters<typeof isMatchWithinAreaBounds>[0]['detection'];
    segment: Parameters<typeof isMatchWithinAreaBounds>[0]['segment'];
  },
>(
  matches: T[],
  bounds: MatchAreaBounds,
  {
    intrinsics,
    cameraPose,
    surfacePoseOf,
  }: {
    intrinsics: CameraIntrinsics;
    cameraPose: CartesianPose | undefined;
    /**
     * The pose projection returned, before accuracy calibration or a top-down
     * override: the one frame that matches the uncorrected camera pose and
     * still has the measured surface as its X axis.
     */
    surfacePoseOf: (match: T) => CartesianPose;
  },
): T[] {
  if (!cameraPose) {
    log.warn(
      'filterProjectedMatchesByArea',
      'No wrist pose for the frame; the match size bounds are not applied',
    );

    return matches;
  }

  const kept = matches.filter((match) =>
    isMatchWithinAreaBounds(
      { detection: match.blob, segment: match.segment },
      bounds,
      {
        plane: surfacePlaneAtPose(surfacePoseOf(match)),
        cameraPose,
        intrinsics,
      },
    ),
  );

  if (kept.length !== matches.length) {
    log.info(
      'filterProjectedMatchesByArea',
      `Match size bounds (${describeMatchAreaBounds(bounds)}) dropped ${
        matches.length - kept.length
      } of ${matches.length} detections`,
    );
  }

  return kept;
}

async function get2DBlobs(args: RunLocateArgs): Promise<{
  blobs: LocateMatch[];
  image: ArrayBuffer;
  // The arm pose that goes with `image`. See `sampleWristPoseForFrame`.
  wristPoseAtCapture: CartesianPose | undefined;
  // Set only for the DinoFast method when `returnHeatmap` was requested
  // (preview overlay); null/undefined for every other method or when not asked.
  heatmap?: DinoFastDenseHeatmap | null;
}> {
  const {
    camera,
    method,
    regionOfInterest,
    vision,
    equipment,
    getState,
    wristPoseAtCapture: callerWristPose,
    returnHeatmap = false,
    preview,
  } = args;

  if (!camera.equipment) {
    throw new Error('Camera equipment is required for get2DBlobs');
  }

  const wristPoseAtCapture = resolveWristPoseAtCapture({
    getState,
    wristPoseAtCapture: callerWristPose,
  });

  const image = await getCameraColorFrame(camera.equipment, camera.integration);

  const areaProjection = await resolveMatchAreaProjection(
    args,
    wristPoseAtCapture,
  );

  let blobs: LocateMatch[];
  let heatmap: DinoFastDenseHeatmap | null = null;

  switch (method.kind) {
    case 'BlobDetection2D': {
      const { disableRotation, ...visionParams } = method.settings;

      blobs = await vision.detect2DBlobs(image, regionOfInterest, visionParams);

      if (disableRotation) {
        blobs = applyDisableRotation(blobs);
      }

      break;
    }
    case 'ShapeDetection2D': {
      const { disableRotation, ...visionParams } = method.settings;

      blobs = await vision.detect2DShapes(
        image,
        method.templateImage,
        regionOfInterest,
        visionParams,
      );

      if (disableRotation) {
        blobs = applyDisableRotation(blobs);
      }

      break;
    }
    case 'TextPromptDetection2D': {
      blobs = await getTextPromptBlobs({
        image,
        regionOfInterest,
        params: method.settings,
        equipment,
        vision,
        areaProjection,
      });

      break;
    }
    // This locates the center of a the chessboard used for accuracy calibration
    case 'ChessboardDetection2D': {
      const result = await vision.getChessboardCorners(image, {
        kind: 'chessboard',
        rows: 3,
        cols: 3,
        squareSize: 0.03426,
      });

      blobs = [
        {
          detection: {
            x: result.corners[4].x, // center corner
            y: result.corners[4].y, // center corner
            width: 1,
            height: 1,
            score: 0,
            rotation: 0,
          },
          segment: null,
        },
      ];

      break;
    }
    case 'CustomCheckpointDetection2D': {
      blobs = await getCustomCheckpointBlobs({
        image,
        regionOfInterest,
        method,
        equipment,
        vision,
        preview,
      });

      break;
    }
    case 'ManualBoundingBox': {
      blobs = [
        {
          detection: {
            x: method.settings.cx,
            y: method.settings.cy,
            width: method.settings.width,
            height: method.settings.height,
            score: 1.0,
            rotation: 0,
          },
          segment: null,
        },
      ];

      break;
    }
    case 'DinoFast': {
      // The locate path normally skips the dense heatmap (a preview-only
      // payload), but the ClickFind Locate step in the routine editor sets
      // `returnHeatmap` so it can render the same heatmap overlay as the Teach
      // page. Routine execution leaves it off.
      ({ matches: blobs, heatmap } = await getDinoFastBlobs({
        image,
        regionOfInterest,
        params: method.settings,
        equipment,
        vision,
        returnHeatmap,
        areaProjection,
      }));

      break;
    }
    default:
      throw new Error(`Unsupported method ${(method as LocateMethod).kind}`);
  }

  return { blobs, image, wristPoseAtCapture, heatmap };
}

async function getIntrinsics(
  intrinsics: CameraIntrinsics | CameraIntrinsicsExtended | undefined,
  equipment: Camera | WristCamera,
): Promise<CameraIntrinsics | CameraIntrinsicsExtended> {
  let out = intrinsics;

  if (!out) {
    const config = equipment.getConfig() as
      | CameraConfiguration
      | WristCameraConfiguration;

    try {
      out = await equipment.getIntrinsics();
    } catch {
      // Calibrated intrinsics not available — fall back to built-in (e.g. camera-bot-cpp RPC)
      log.warn(
        'getIntrinsics',
        'No calibrated intrinsics, trying built-in intrinsics',
      );

      out = await equipment.getBuiltInIntrinsics();
    }

    if (out == null) {
      log.error('getIntrinsics', 'No intrinsics found for camera', {
        config,
      });

      throw new Error(`No intrinsics found for camera ${config.name}`);
    }

    log.info('getIntrinsics', 'Using intrinsics from camera', {
      intrinsics: out,
      config,
    });
  } else {
    log.info('getIntrinsics', 'Using intrinsics from args', {
      intrinsics: out,
    });
  }

  return out;
}

/**
 * Select the projection oneof arm for the operator's chosen method. The
 * discriminator maps onto the arms 1:1, so this is a selection rather than a
 * translation — `Detection3D` never reaches here, having been routed to its own
 * RPC by the caller before any of this runs.
 */
function buildProjectionMethod(
  method: Exclude<ProjectionMethod, 'Detection3D'>,
  plane: Plane | undefined,
  objectHeight: number,
  ransacParams?: RunLocateArgs['ransacParams'],
  maxTiltAngle?: number,
  minInlierRatio?: number,
  fallbackToTopDown?: boolean,
): RunProjectionRequest['method'] {
  switch (method) {
    case 'ManualPlane': {
      if (!plane) {
        throw new Error(
          'Plane is required for the ManualPlane projection method. Ensure a workplane is configured.',
        );
      }

      return {
        manualPlane: {
          origin: {
            x: plane.origin.x,
            y: plane.origin.y,
            z: plane.origin.z,
          },
          plusX: { x: plane.plusX.x, y: plane.plusX.y, z: plane.plusX.z },
          plusY: { x: plane.plusY.x, y: plane.plusY.y, z: plane.plusY.z },
          objectHeight,
        },
      };
    }

    case 'DetectedPlane':
      return {
        detectedPlane: {
          maxIterations:
            ransacParams?.maxIterations ?? DEFAULT_RANSAC_MAX_ITERATIONS,
          distanceThreshold:
            ransacParams?.distanceThreshold ??
            DEFAULT_RANSAC_DISTANCE_THRESHOLD,
          minSamples: ransacParams?.minSamples ?? DEFAULT_RANSAC_MIN_SAMPLES,
          maxTiltAngle,
          minInlierRatio,
          fallbackToTopDown,
        },
      };

    // Carries no parameters because it fits nothing: the fit settings the
    // operator has saved stay saved, but none of them describe what runs here.
    case 'DetectedDepth':
      return { detectedDepth: {} };

    default:
      throw new Error(`Unknown projection method: ${method}`);
  }
}

function buildProjectionRequest(
  blobs: LocateMatch[],
  image: ArrayBuffer,
  intrinsics: CameraIntrinsics | CameraIntrinsicsExtended,
  extrinsics: ExtrinsicsKinematics,
  method: RunProjectionRequest['method'],
  wristPoseAtCapture: CartesianPose | undefined,
  graspFrame: RunLocateArgs['graspFrame'],
  orientationReference: RunLocateArgs['orientationReference'],
  depthImage?: ArrayBuffer,
  includeDebugVisualization?: boolean,
): RunProjectionRequest {
  const extended = intrinsics as CameraIntrinsicsExtended;

  return {
    // Map zod LocateMatch (segment: null) → proto LocateMatch (segment: undefined)
    detections: blobs.map((m) => ({
      ...m,
      segment: m.segment ?? undefined,
    })),
    image: new Uint8Array(image),
    depthImage: depthImage ? new Uint8Array(depthImage) : undefined,
    cameraParams: {
      intrinsics: {
        width: intrinsics.width,
        height: intrinsics.height,
        fx: intrinsics.fx,
        fy: intrinsics.fy,
        ppx: intrinsics.ppx,
        ppy: intrinsics.ppy,
        distortionCoefficients: extended.distortionCoefficients ?? [],
        distortionModel: extended.distortionModel ?? undefined,
      },
      extrinsics: extrinsics.extrinsics,
      extrinsicsKind:
        extrinsics.kind === 'tcp' ? ExtrinsicsKind.TCP : ExtrinsicsKind.BASE,
      robotPose: requireRobotPoseForExtrinsics(
        extrinsics.kind,
        wristPoseAtCapture,
      ),
    },
    method,
    graspFrame: GRASP_FRAME_MAP[graspFrame ?? 'object'],
    // Map the persisted [x, y] pairs back to Point2D[] for the wire. Left
    // unset when no reliable reference was taught (null) ⇒ vision-bot falls
    // back to plane orientation.
    orientationReference: orientationReference
      ? {
          referenceContour: orientationReference.referenceContour.map(
            ([x, y]) => ({ x, y }),
          ),
        }
      : undefined,
    includeDebugVisualization,
  };
}

// Map a vision-bot proto Blob2D (Detection3D.segment) back to the zod Blob2D
// shape used by LocateResult/LocateMatch. Returns null when the proto segment
// is absent, so the box-only overlay path is preserved. meanIntensity and
// circularity are detect-time only and not carried on the wire, so they're
// omitted here.
function protoSegmentToBlob2D(segment: ProtoBlob2D | undefined): Blob2D | null {
  if (!segment) return null;

  return {
    score: segment.score,
    contour: segment.contour.map((p) => ({ x: p.x, y: p.y })),
    area: segment.area ?? undefined,
    classId: segment.classId ?? undefined,
    className: segment.className ?? undefined,
    // Cast to the zod-inferred field type rather than `Uint8Array<ArrayBuffer>`:
    // the proto bytes are `Uint8Array<ArrayBufferLike>` while Blob2D infers
    // `Uint8Array<ArrayBuffer>` under TS >=5.7, so the bare assignment trips
    // TS2322 in the api-srv Cloud Run deploy build (which installs a newer TS).
    // Targeting `Blob2D['maskRle']` stays valid under both that TS and the
    // local/pinned TS 5.1.3 where `Uint8Array` is non-generic.
    maskRle: (segment.maskRle ?? undefined) as Blob2D['maskRle'],
    maskWidth: segment.maskWidth ?? undefined,
    maskHeight: segment.maskHeight ?? undefined,
  };
}

// Maps the runLocate `graspFrame` arg to the vision-bot proto enum. Defaults to
// OBJECT (the detection principal axis) when the frame is omitted.
const GRASP_FRAME_MAP: Record<
  NonNullable<RunLocateArgs['graspFrame']>,
  GraspFrame
> = {
  object: GraspFrame.OBJECT,
  plane: GraspFrame.PLANE,
  world: GraspFrame.WORLD,
};

const POSE_METHOD_MAP: Record<string, PoseEstimationMethod> = {
  OBB_DEPTH: PoseEstimationMethod.OBB_DEPTH,
  OBB_ICP: PoseEstimationMethod.OBB_ICP,
  FPFH_ICP: PoseEstimationMethod.FPFH_ICP,
};

const POSE_METHODS_USING_REFERENCE = new Set(['OBB_ICP', 'FPFH_ICP']);

/**
 * Create RLE-encoded bytes for a rectangular mask from a bounding box.
 * Used as a SAM2-free fallback: generates a filled-rectangle mask so that
 * Run3DPoseEstimation can segment depth within the detection bbox.
 *
 * Format: 1 byte (start value 0 or 1), then 4-byte LE uint32 run lengths,
 * alternating between the start value and its complement.
 */
function makeRectMaskRle(
  cx: number,
  cy: number,
  w: number,
  h: number,
  imgW: number,
  imgH: number,
): Uint8Array {
  const x1 = Math.max(0, Math.floor(cx - w / 2));
  const y1 = Math.max(0, Math.floor(cy - h / 2));
  const x2 = Math.min(imgW, Math.floor(cx + w / 2));
  const y2 = Math.min(imgH, Math.floor(cy + h / 2));

  if (x2 <= x1 || y2 <= y1) {
    return new Uint8Array(0);
  }

  const rectW = x2 - x1;
  const numRows = y2 - y1;
  const rowGap = imgW - rectW;

  const leadingZeros = y1 * imgW + x1;
  const trailingZeros = imgW - x2 + (imgH - y2) * imgW;

  const runLengths: number[] = [];
  const startValue = leadingZeros > 0 ? 0 : 1;

  if (leadingZeros > 0) {
    runLengths.push(leadingZeros);
  }

  if (rowGap === 0) {
    // Rectangle spans full width — ones are contiguous
    runLengths.push(rectW * numRows);
  } else {
    for (let row = 0; row < numRows; row += 1) {
      runLengths.push(rectW);

      if (row < numRows - 1) {
        runLengths.push(rowGap);
      }
    }
  }

  if (trailingZeros > 0) {
    runLengths.push(trailingZeros);
  }

  const buffer = new ArrayBuffer(1 + 4 * runLengths.length);
  const view = new DataView(buffer);
  view.setUint8(0, startValue);

  let offset = 1;

  for (const len of runLengths) {
    view.setUint32(offset, len, true);
    offset += 4;
  }

  return new Uint8Array(buffer);
}

export interface Estimate3DPosesArgs {
  /**
   * 2D matches (detector box + optional mask segment) feeding the pose
   * estimator. This is the proto `LocateMatch` shape (assigned directly to
   * `Run3DPoseEstimationRequest.detections`), not the zod types from
   * LocateTypes — the two differ in `maskRle`'s typed array generic
   * (`Uint8Array<ArrayBufferLike>` vs `Uint8Array<ArrayBuffer>`).
   */
  blobs: Run3DPoseEstimationRequest['detections'];
  /** Raw depth frame. Copied to a Uint8Array before the RPC. */
  depthImage: ArrayBuffer | Uint8Array;
  /** Camera intrinsics/extrinsics + robot pose for unprojection. */
  cameraParams: CameraParams;
  /** Shared 3D pose-estimation tuning (poseMethod, cadSource, thresholds…). */
  config: Detection3DPoseEstimationConfig;
  /** Vision client used to call run3DPoseEstimation. */
  vision: VisionInterface;
  /** When true, vision-bot renders a debug overlay into the response. */
  includeDebugVisualization?: boolean;
}

export interface Estimate3DPosesResult {
  detections: Detection3D[];
  debugVisualization?: Uint8Array;
}

/**
 * Stage 2 of the 3D locate pipeline: given already-computed 2D detections
 * (with masks), a depth frame, and camera parameters, build a
 * `Run3DPoseEstimationRequest` and call `vision.run3DPoseEstimation` to get
 * 6D poses back.
 *
 * Extracted verbatim from `run3DLocate` so callers that produce their own 2D
 * blobs (e.g. the LocateSkill's 3D-detection projection) can reuse the exact
 * same pose-estimation request construction and response handling.
 *
 * The objectReference / CAD pose-options logic, tuning defaults, and response
 * parsing are byte-for-byte identical to the original inline block — only the
 * inputs are parameterized.
 */
export async function estimate3DPosesFromBlobs(
  args: Estimate3DPosesArgs,
): Promise<Estimate3DPosesResult> {
  const { blobs, depthImage, cameraParams, config, vision } = args;

  const poseRequest: Run3DPoseEstimationRequest = {
    detections: blobs,
    depthImage: new Uint8Array(depthImage),
    cameraParams,
    poseMethod:
      POSE_METHOD_MAP[config.poseMethod] ?? PoseEstimationMethod.OBB_DEPTH,
    objectReference:
      POSE_METHODS_USING_REFERENCE.has(config.poseMethod) &&
      config.cadSource === 'PRIMITIVE'
        ? {
            primitiveShape: {
              shapeType:
                config.primitiveShapeType === 'CYLINDER'
                  ? PrimitiveShapeType.CYLINDER
                  : PrimitiveShapeType.BOX,
              dimX: config.primitiveDimX,
              dimY: config.primitiveDimY,
              dimZ: config.primitiveDimZ,
            },
          }
        : undefined,
    confidenceThreshold: config.confidenceThreshold,
    maxDetections: config.maxDetections,
    depthOutlierPercentile: config.depthOutlierPercentile,
    // FPFH-specific tuning; ignored by OBB_DEPTH / OBB_ICP in vision-bot.
    ransacRestarts:
      config.poseMethod === 'FPFH_ICP' ? config.fpfhRansacRestarts : undefined,
    voxelSize:
      config.poseMethod === 'FPFH_ICP' ? config.fpfhVoxelSize : undefined,
    includeDebugVisualization: args.includeDebugVisualization,
  };

  log.debug('estimate3DPoses.request', 'Sending 3D pose estimation request', {
    detectionCount: poseRequest.detections.length,
    poseMethod: config.poseMethod,
    depthImageBytes: poseRequest.depthImage.length,
  });

  if (
    POSE_METHODS_USING_REFERENCE.has(config.poseMethod) &&
    config.cadSource === 'CAD_FILE'
  ) {
    if (!config.cadModelStorageID || !config.cadModelFormat) {
      throw new Error(
        'CAD_FILE reference is selected but no CAD model has been uploaded. ' +
          'Upload a .stl, .obj, or .ply file in the 3D Detection settings, ' +
          'or pick a different reference model.',
      );
    }
  }

  const poseOptions =
    POSE_METHODS_USING_REFERENCE.has(config.poseMethod) &&
    config.cadSource === 'CAD_FILE' &&
    config.cadModelStorageID &&
    config.cadModelFormat
      ? {
          cadModelStorageID: config.cadModelStorageID,
          cadModelFormat: config.cadModelFormat,
          cadModelUnits: config.cadModelUnits,
        }
      : undefined;

  const poseResponse = await vision.run3DPoseEstimation(
    poseRequest,
    poseOptions,
  );

  if (poseResponse.error) {
    throw new Error(`3D pose estimation failed: ${poseResponse.error.message}`);
  }

  if (!poseResponse.data) {
    throw new Error('3D pose estimation returned no data');
  }

  log.info('estimate3DPoses.result', '3D pose estimation completed', {
    detectionCount: poseResponse.data.detections.length,
    processingTimeMs: poseResponse.data.processingTimeMs,
  });

  return {
    detections: poseResponse.data.detections,
    debugVisualization: poseResponse.data.debugVisualization,
  };
}

/**
 * Convert detected 2D matches into proto LocateMatch detections suitable for 3D
 * pose estimation. Drops sub-5px detections (by detector box), preserves the
 * SAM2 mask segment when present, and falls back to a rectangular bbox mask
 * (sized to the full image, derived from the detector box) otherwise.
 *
 * Extracted verbatim from the CPU path of `run3DLocate` so the LocateSkill
 * 3D-detection projection can reuse identical conversion + mask-fallback logic.
 */
export function blobsToDetectionsWithMasks(
  matches: LocateMatch[],
  imgWidth: number,
  imgHeight: number,
): Run3DPoseEstimationRequest['detections'] {
  return matches
    .filter(({ detection }) => detection.width >= 5 && detection.height >= 5)
    .map(({ detection, segment }) => ({
      detection: {
        x: detection.x,
        y: detection.y,
        width: detection.width,
        height: detection.height,
        rotation: detection.rotation,
        score: detection.score,
        classId: detection.classId,
        className: detection.className,
      },
      // Segment carries the contour + mask. Use the SAM2 segment's mask when
      // present, else synthesize a rectangular mask from the detector box so
      // Run3DPoseEstimation can segment depth within the bbox.
      segment: {
        score: detection.score,
        contour: segment?.contour ?? [],
        area: segment?.area,
        classId: detection.classId,
        className: detection.className,
        maskRle:
          segment?.maskRle ??
          makeRectMaskRle(
            detection.x,
            detection.y,
            detection.width,
            detection.height,
            imgWidth,
            imgHeight,
          ),
        maskWidth: segment?.maskWidth ?? imgWidth,
        maskHeight: segment?.maskHeight ?? imgHeight,
      },
    }));
}

/**
 * Build the `CameraParams` (intrinsics, extrinsics, extrinsics kind, robot
 * pose) needed for 3D pose estimation. Extracted verbatim from "Step 3" of
 * `run3DLocate` so both the Detection3D method path and the 2D-method
 * 3D-detection projection path share identical camera-param construction.
 */
async function buildCameraParams(
  args: RunLocateArgs,
  wristPoseAtCapture: CartesianPose | undefined,
  preResolvedIntrinsics?: CameraIntrinsics | CameraIntrinsicsExtended,
): Promise<{
  cameraParams: CameraParams;
  cameraPose: CartesianPose | undefined;
}> {
  if (!args.camera.equipment) {
    throw new Error('Camera equipment is required for buildCameraParams');
  }

  // Callers that already fetched intrinsics (e.g. for mask dims) pass them in
  // to avoid a redundant camera round-trip for the identical value.
  const intrinsics =
    preResolvedIntrinsics ??
    (await getIntrinsics(args.intrinsics, args.camera.equipment));

  const extrinsics = await resolveVisionExtrinsics(
    args.camera.equipment,
    args.extrinsics,
  );

  const extended = intrinsics as CameraIntrinsicsExtended;

  const cameraParams: CameraParams = {
    intrinsics: {
      width: intrinsics.width,
      height: intrinsics.height,
      fx: intrinsics.fx,
      fy: intrinsics.fy,
      ppx: intrinsics.ppx,
      ppy: intrinsics.ppy,
      distortionCoefficients: extended.distortionCoefficients ?? [],
      distortionModel: extended.distortionModel ?? undefined,
    },
    extrinsics: extrinsics.extrinsics,
    extrinsicsKind:
      extrinsics.kind === 'tcp' ? ExtrinsicsKind.TCP : ExtrinsicsKind.BASE,
    robotPose: requireRobotPoseForExtrinsics(
      extrinsics.kind,
      wristPoseAtCapture,
    ),
  };

  return {
    cameraParams,
    cameraPose: cameraPoseAtCapture(extrinsics, wristPoseAtCapture),
  };
}

/**
 * Post-process raw Detection3D results into ordered, limited `LocateResult[]`.
 *
 * Extracted verbatim from "Step 4.5 + Step 5" of `run3DLocate`:
 *   - drop detections below `detection3dConfig.minInlierRatio`,
 *   - apply accuracy-calibration correction to each pose,
 *   - apply the user-defined transform relative to the detected pose,
 *   - convert to LocateResult (blob + pose + extents),
 *   - sort by `resultOrdering` and slice to `resultsLimit`.
 *
 * Shared by the Detection3D method path and the 2D-method 3D-detection
 * projection path; both pass the relevant `Detection3DPoseEstimationConfig`
 * (the Detection3D method's `settings` or the projection `detection3dConfig`).
 */
export function finalizeDetection3DResults(params: {
  rawDetections: Detection3D[];
  image: ArrayBuffer;
  debugVisualization?: Uint8Array;
  config: Detection3DPoseEstimationConfig;
  args: RunLocateArgs;
}): {
  results: LocateResult[];
  image: ArrayBuffer;
  debugVisualization?: Uint8Array;
} {
  const { rawDetections, image, debugVisualization, config, args } = params;

  // Step 4.5: Drop detections whose ICP/RANSAC fit falls below the
  // user-configured minimum. A misaligned pose is worse than no pose —
  // downstream motion and visualization see the filtered set only.
  const minInlierRatio = config.minInlierRatio ?? 0;

  const keptDetections =
    minInlierRatio > 0
      ? rawDetections.filter((detection) => {
          const fit = detection.inlierRatio;
          if (fit === undefined || fit === null) return true;

          return fit >= minInlierRatio;
        })
      : rawDetections;

  if (minInlierRatio > 0 && keptDetections.length !== rawDetections.length) {
    log.info('finalizeDetection3D.filter', 'Filtered low-fit detections', {
      minInlierRatio,
      beforeCount: rawDetections.length,
      afterCount: keptDetections.length,
    });
  }

  // Step 5: Convert Detection3D results to LocateResult[]
  const blobsAndPoses = keptDetections
    .map((detection) => {
      if (!detection.pose || !detection.blob2d) {
        return undefined;
      }

      let pose: CartesianPose = {
        x: detection.pose.x,
        y: detection.pose.y,
        z: detection.pose.z,
        i: detection.pose.i,
        j: detection.pose.j,
        k: detection.pose.k,
        w: detection.pose.w,
      };

      if (args.accuracyCalibration) {
        const cameraCorrection = getCalibrationCorrection(
          pose,
          args.accuracyCalibration,
        );

        pose = applyCompoundPose(pose, cameraCorrection);
      }

      // The offset always rides in the detected pose's own display frame
      // (vision-bot already encodes the grasp orientation in the pose).
      const effectivePlane = poseToDisplayFrame(pose);

      let poseBeforeOffset: CartesianPose | undefined;

      if (args.transform) {
        poseBeforeOffset = pose;
        pose = applyUserDefinedTransform(args.transform, pose, effectivePlane);
      }

      return {
        // The result carries the detector BOX (Detection2D). Detection3D.blob2d
        // is now a box-only Detection2D — no contour is threaded this far.
        blob: {
          x: detection.blob2d.x,
          y: detection.blob2d.y,
          width: detection.blob2d.width,
          height: detection.blob2d.height,
          rotation: detection.blob2d.rotation,
          score: detection.blob2d.score,
          classId: detection.blob2d.classId,
          className: detection.blob2d.className,
        },
        pose,
        poseBeforeOffset,
        extents: detection.extents
          ? {
              x: detection.extents.x,
              y: detection.extents.y,
              z: detection.extents.z,
            }
          : undefined,
        segment: protoSegmentToBlob2D(detection.segment),
      };
    })
    .filter(isNotUndefined);

  // Apply result ordering and limit (consistent with regular locate path).
  // sortBlobs operates on LocateMatch; wrap each result's box as a segment-less
  // match for the comparators (no contour exists post-pose-estimation).
  const sortedBlobs = sortBlobs(
    blobsAndPoses.map((item) => ({ detection: item.blob, segment: null })),
    args.resultOrdering,
  );

  const sortedResults = applyPostProjectionOrdering(
    sortedBlobs
      .map((match) =>
        blobsAndPoses.find((item) => item.blob === match.detection),
      )
      .filter(isNotUndefined),
    args.resultOrdering,
  ).slice(0, args.resultsLimit ?? LOCATE_DEFAULT_RESULT_LIMIT);

  log.info(
    'finalizeDetection3D.transformed',
    'Transformed 3D blobs',
    sortedResults.map(stripSegmentForLog),
  );

  return {
    results: sortedResults,
    image,
    debugVisualization,
  };
}

/**
 * 3D locate path: text prompt → masks → depth → OBB pose estimation.
 * Bypasses plane projection entirely; uses Run3DPoseEstimation RPC.
 *
 * When a GPU Task Runner is available, uses SAM2 for precise segmentation masks.
 * Without a GPU, falls back to rectangular bounding-box masks and calls
 * vision-bot directly (GroundingDINO runs on CPU).
 */
async function run3DLocate(args: RunLocateArgs): Promise<{
  results: LocateResult[];
  image: ArrayBuffer;
  debugVisualization?: Uint8Array;
  cameraPose?: CartesianPose;
}> {
  if (args.method.kind !== 'Detection3D') {
    throw new Error('run3DLocate requires Detection3D method');
  }

  const { settings } = args.method;

  if (!args.camera.equipment) {
    throw new Error('Camera equipment is required for 3D locate');
  }

  if (!args.depthImage) {
    throw new Error('Depth image is required for 3D locate');
  }

  // Step 1: Get color frame, paired with the pose it was taken at. Steps 2-3
  // below run a full detection round trip before the pose is used, so reading
  // it there would pair the image with wherever the arm had moved to by the
  // time inference returned.
  //
  // args.depthImage was fetched by the caller, before this function was
  // entered, and on these paths it is the geometry robotPose lifts into the
  // base frame — so the caller's pose is the one that matches it. Sampling
  // here would already be one depth fetch late.
  const wristPoseAtCapture = resolveWristPoseAtCapture(args);

  const image = await getCameraColorFrame(
    args.camera.equipment,
    args.camera.integration,
  );

  // Step 2: Run 2D detection — GPU path uses SAM2 masks, CPU path uses bbox masks
  let detections: Run3DPoseEstimationRequest['detections'];

  // The CPU path fetches intrinsics for the rect-mask fallback; reuse that same
  // value for buildCameraParams (Step 3) instead of fetching twice. The GPU
  // path leaves this undefined and lets buildCameraParams fetch internally.
  let cpuIntrinsics: CameraIntrinsics | CameraIntrinsicsExtended | undefined;

  if (args.gpuTaskRunner) {
    // GPU path: SAM2 segmentation masks via GPU Task Runner
    const { croppedImage, offsetX, offsetY } =
      await args.vision.preprocessImage(image, args.regionOfInterest);

    const base64Image = Buffer.from(croppedImage).toString('base64');

    const inferenceResponse = await args.gpuTaskRunner.executeVisionCommand({
      command: 'run_inference',
      image: base64Image,
      model_path: 'text_prompt',
      model_type: 'TextPrompt',
      thresholding_params: {
        prompt: settings.textPrompt,
        use_sam: 'true',
        box_threshold: settings.boxThreshold.toString(),
        text_threshold: settings.textThreshold.toString(),
      },
      include_masks: true,
    });

    if (!inferenceResponse || typeof inferenceResponse === 'undefined') {
      throw new Error('2D detection returned no response');
    }

    const response2D = inferenceResponse as ProtoInferenceResponse;

    if (response2D.error) {
      throw new Error(`2D detection failed: ${response2D.error.message}`);
    }

    if (!response2D.data) {
      throw new Error('2D detection returned no data');
    }

    const protoBlobs = response2D.data.blobs;

    log.info(
      'run3DLocate.2d.gpu',
      `2D detection found ${protoBlobs.length} blobs`,
      { processingTimeMs: response2D.data.processingTimeMs },
    );

    // Adjust match coordinates for ROI offset: shift the detector box center
    // and (when present) every segment contour point back to full-frame space.
    // Inlined rather than shiftMatchByOffset: this is the proto-typed
    // Run3DPoseEstimationRequest detection where detection/segment may be
    // undefined off the wire (the zod LocateMatch requires detection).
    detections = protoBlobs.map((match) => ({
      ...match,
      detection: match.detection
        ? {
            ...match.detection,
            x: match.detection.x + offsetX,
            y: match.detection.y + offsetY,
          }
        : undefined,
      segment: match.segment
        ? {
            ...match.segment,
            contour: match.segment.contour.map((p) => ({
              x: p.x + offsetX,
              y: p.y + offsetY,
            })),
          }
        : undefined,
    }));
  } else {
    // CPU path: vision-bot direct (EfficientSAM3, which detects and segments
    // in one model).
    log.info(
      'run3DLocate.2d.cpu',
      'No GPU Task Runner, using vision-bot directly with EfficientSAM3 masks',
    );

    const blobs = await args.vision.detect2DTextPrompt(
      image,
      args.regionOfInterest,
      {
        gpuTaskRunnerId: '',
        textInput: settings.textPrompt,
        // Detection3DParams still carries separate box/text thresholds;
        // collapse via Math.max so the effective filter matches the
        // pre-nuke behavior where a detection had to clear both thresholds
        // independently (effective filter was score >= max(box, text)).
        // Dual thresholds come back in the multi-class follow-up PR.
        confidenceThreshold: Math.max(
          settings.boxThreshold,
          settings.textThreshold,
        ),
        useSam: true,
      },
    );

    log.info('run3DLocate.2d.cpu', `2D detection found ${blobs.length} blobs`);

    // Need image dimensions for rect mask fallback
    cpuIntrinsics = await getIntrinsics(args.intrinsics, args.camera.equipment);

    // Convert to proto Blob2D format; use SAM2 masks when available,
    // fall back to rectangular bbox masks if SAM2 didn't return masks.
    detections = blobsToDetectionsWithMasks(
      blobs,
      cpuIntrinsics.width,
      cpuIntrinsics.height,
    );
  }

  if (detections.length === 0) {
    return { results: [], image };
  }

  // Step 3: Get camera params (reuse the CPU-path intrinsics when present)
  const { cameraParams, cameraPose } = await buildCameraParams(
    args,
    wristPoseAtCapture,
    cpuIntrinsics,
  );

  // Step 4: Build and send 3D pose estimation request (shared with the
  // LocateSkill 3D-detection path via estimate3DPosesFromBlobs).
  const { detections: rawDetections, debugVisualization } =
    await estimate3DPosesFromBlobs({
      blobs: detections,
      depthImage: args.depthImage,
      cameraParams,
      config: settings,
      vision: args.vision,
      includeDebugVisualization: args.includeDebugVisualization,
    });

  // Step 4.5 + Step 5: filter low-fit detections, apply calibration +
  // user transform, convert to LocateResult[], order and limit. Shared with
  // the 2D-method 3D-detection projection path.
  return {
    ...finalizeDetection3DResults({
      rawDetections,
      image,
      debugVisualization,
      config: settings,
      args,
    }),
    cameraPose,
  };
}

/**
 * 2D-method 3D-detection projection path: run the skill's own 2D detection
 * method (Blob/Shape/TextPrompt/CustomCheckpoint/DinoFast), then estimate a
 * 6D pose per detection from depth via the Detection3D backend — instead of
 * projecting blobs onto the taught plane.
 *
 * Reuses the same camera-param construction, mask fallback, pose estimation,
 * and post-processing as the Detection3D method path.
 */
async function run2DMethodWith3DProjection(args: RunLocateArgs): Promise<{
  results: LocateResult[];
  image: ArrayBuffer;
  debugVisualization?: Uint8Array;
  cameraPose?: CartesianPose;
}> {
  if (!args.camera.equipment) {
    throw new Error('Camera equipment is required for 3D-detection projection');
  }

  if (!args.depthImage) {
    throw new Error('Depth image is required for 3D-detection projection');
  }

  if (!args.detection3dConfig) {
    throw new Error(
      'detection3dConfig is required for 3D-detection projection',
    );
  }

  const { blobs, image, wristPoseAtCapture } = await get2DBlobs(args);

  if (blobs.length === 0) {
    return { results: [], image };
  }

  // Need image dimensions for the rectangular mask fallback.
  const intrinsics = await getIntrinsics(
    args.intrinsics,
    args.camera.equipment,
  );

  const detections = blobsToDetectionsWithMasks(
    blobs,
    intrinsics.width,
    intrinsics.height,
  );

  if (detections.length === 0) {
    return { results: [], image };
  }

  // Reuse the intrinsics already fetched for the mask dims (no second round-trip).
  const { cameraParams, cameraPose } = await buildCameraParams(
    args,
    wristPoseAtCapture,
    intrinsics,
  );

  const { detections: rawDetections, debugVisualization } =
    await estimate3DPosesFromBlobs({
      blobs: detections,
      depthImage: args.depthImage,
      cameraParams,
      config: args.detection3dConfig,
      vision: args.vision,
      includeDebugVisualization: args.includeDebugVisualization,
    });

  return {
    ...finalizeDetection3DResults({
      rawDetections,
      image,
      debugVisualization,
      config: args.detection3dConfig,
      args,
    }),
    cameraPose,
  };
}

export async function runLocate(args: RunLocateArgs): Promise<{
  results: LocateResult[];
  image: ArrayBuffer;
  debugVisualization?: Uint8Array;
  // DinoFast preview only (set when args.returnHeatmap): the dense score
  // heatmap from the 2D-detection pass. The 3D pipelines below don't surface
  // it — ClickFind in the routine editor uses the Manual plane path here.
  heatmap?: DinoFastDenseHeatmap | null;
  // The camera's pose in the robot frame for the frame the results came from,
  // to draw them back onto it. Undefined for a wrist camera whose capture pose
  // is unknown, and when nothing was detected.
  cameraPose?: CartesianPose;
}> {
  // 3D detection has its own pipeline (2D + masks → 3D pose estimation)
  if (args.method.kind === 'Detection3D') {
    return run3DLocate(args);
  }

  // 2D method + 3D-detection projection: run the chosen 2D method, then
  // estimate a 6D pose per detection from depth instead of plane projection.
  if (args.projectionMethod === 'Detection3D') {
    return run2DMethodWith3DProjection(args);
  }

  const {
    blobs: blobs2D,
    image,
    heatmap,
    wristPoseAtCapture,
  } = await get2DBlobs(args);

  let cameraCorrection = ZERO_POSE;

  if (!args.camera.equipment) {
    throw new Error('Camera equipment is required for runLocate');
  }

  const intrinsics = await getIntrinsics(
    args.intrinsics,
    args.camera.equipment,
  );

  const extrinsics = await resolveVisionExtrinsics(
    args.camera.equipment,
    args.extrinsics,
  );

  log.info('blobs', 'Blobs detected', blobs2D.map(stripSegmentForLog));

  const sortedBlobs2D = sortBlobs(blobs2D, args.resultOrdering);

  // `Detection3D` was diverted to its own RPC above, so only the three
  // projection-oneof arms can reach the rest of this function.
  const projectionMethod = (args.projectionMethod ?? 'ManualPlane') as Exclude<
    ProjectionMethod,
    'Detection3D'
  >;

  // ManualPlane matches were measured on the taught plane in `get2DBlobs`. The
  // depth projections only know the surface a match sits on once it has been
  // projected, so their size bounds apply afterwards.
  const depthAreaBounds =
    projectionMethod === 'ManualPlane'
      ? undefined
      : activeMatchAreaBounds(args.method);

  const resultsLimit = args.resultsLimit ?? LOCATE_DEFAULT_RESULT_LIMIT;

  // The limit is applied here, in 2D, to bound projection cost — only the
  // survivors get projected. For `world-z-desc` that height is not known yet
  // (sortBlobs is a no-op for it), so the cap keeps the first N by DETECTION
  // order and `applyPostProjectionOrdering` re-ranks those by Z below. With the
  // default cap of 50 that only matters past 50 detections, which de-stacking
  // does not reach in practice; the Detection3D path (finalizeDetection3DResults)
  // projects first and caps after, since there projection is already done.
  // With depth size bounds the final cap waits until after the filter, so it
  // counts only matches that are kept. Projection still gets a cap, with
  // headroom for the ones the filter drops: TextPrompt has no `maxBlobs` of its
  // own, so nothing else bounds how many boxes reach it.
  const filteredBlobs2D = sortedBlobs2D.slice(
    0,
    depthAreaBounds
      ? resultsLimit * DEPTH_AREA_PROJECTION_HEADROOM
      : resultsLimit,
  );

  if (filteredBlobs2D.length === 0) {
    return { results: [], image, heatmap };
  }

  log.debug('runLocate.method', 'runLocate projection method', {
    projectionMethod,
    rawProjectionMethod: args.projectionMethod,
    hasPlane: !!args.plane,
    hasGpuTaskRunner: !!args.gpuTaskRunner,
    hasDepthImage: !!args.depthImage,
    depthImageBytes: args.depthImage?.byteLength ?? 0,
    includeDebugVisualization: args.includeDebugVisualization,
    blobCount: filteredBlobs2D.length,
  });

  log.info(
    'Accuracy calibration',
    'Accuracy calibration',
    args.accuracyCalibration,
  );

  // Legacy plane-frame reference for the user-defined offset, used only when no
  // grasp frame was explicitly chosen (the standalone non-skill Locate step).
  // ManualPlane: the flat taught-plane pose. The depth methods: base frame
  // (falls through to poseToDisplayFrame below). Preserves saved-routine grasp
  // points from before vision-bot resolved the grasp orientation.
  const planePoseRelativeToBase: CartesianPose =
    projectionMethod === 'ManualPlane' && args.plane
      ? getPlanePose(movePlaneInItsZDirection(args.plane, args.objectHeight))
      : ZERO_POSE;

  const projectionMethodArm = buildProjectionMethod(
    projectionMethod,
    args.plane,
    args.objectHeight,
    args.ransacParams,
    args.maxTiltAngle,
    args.minInlierRatio,
    args.fallbackToTopDown,
  );

  const request = buildProjectionRequest(
    filteredBlobs2D,
    image,
    intrinsics,
    extrinsics,
    projectionMethodArm,
    wristPoseAtCapture,
    args.graspFrame,
    args.orientationReference,
    args.depthImage,
    args.includeDebugVisualization,
  );

  log.debug('projection.request', 'Sending projection request', {
    methodKey: projectionMethodArm
      ? Object.keys(projectionMethodArm)[0]
      : undefined,
    detectionCount: request.detections.length,
    hasDepthImage: !!request.depthImage?.length,
    depthImageBytes: request.depthImage?.length ?? 0,
    includeDebugVisualization: request.includeDebugVisualization,
  });

  let response;

  if (args.gpuTaskRunner) {
    response = await args.gpuTaskRunner.executeProjection(request);
  } else {
    response = await args.vision.runProjection(request);
  }

  if (response.error) {
    throw new Error(`Projection failed: ${response.error.message}`);
  }

  if (!response.data) {
    throw new Error('Projection returned no data');
  }

  log.info('projection', 'Projection completed', {
    detectionCount: response.data.detections.length,
    processingTimeMs: response.data.processingTimeMs,
    hasDebugVisualization: !!response.data.debugVisualization,
    debugVisualizationBytes: response.data.debugVisualization?.length ?? 0,
  });

  // Tilt filtering is handled by vision-bot (via maxTiltAngle on the
  // DetectedPlane arm) so the debug visualization already excludes rejected
  // detections.
  const { detections } = response.data;

  const projectedPoses = new Map<object, CartesianPose>();

  const blobsAndPoses = detections
    .map((detection) => {
      if (!detection.pose || !detection.blob2d) {
        return undefined;
      }

      let pose: CartesianPose = {
        x: detection.pose.x,
        y: detection.pose.y,
        z: detection.pose.z,
        i: detection.pose.i,
        j: detection.pose.j,
        k: detection.pose.k,
        w: detection.pose.w,
      };

      const projectedPose = pose;

      if (args.accuracyCalibration) {
        cameraCorrection = getCalibrationCorrection(
          pose,
          args.accuracyCalibration,
        );

        pose = applyCompoundPose(pose, cameraCorrection);
      }

      // When plane orientation is disabled, keep the RANSAC-derived position
      // but replace orientation with a straight-down approach aligned to
      // the base frame (display X=baseX, Y=baseY, Z=baseZ up).
      if (args.disablePlaneOrientation && projectionMethod !== 'ManualPlane') {
        pose = { ...pose, i: 0.5, j: 0.5, k: -0.5, w: 0.5 };
      }

      // Reference frame the user-defined offset rides in:
      //  - graspFrame set (the skill flow always sets it): the resolved
      //    grasp-pose frame — vision-bot already encodes the grasp orientation
      //    in the pose, so the offset rides in the pose's own display frame.
      //  - graspFrame unset (legacy standalone Locate step): the flat taught-
      //    plane pose frame under ManualPlane (poseToDisplayFrame otherwise), to
      //    preserve saved-routine grasp points from before grasp-frame resolution.
      const useLegacyPlaneFrame =
        args.graspFrame === undefined && projectionMethod === 'ManualPlane';

      const effectivePlane = useLegacyPlaneFrame
        ? planePoseRelativeToBase
        : poseToDisplayFrame(pose);

      let poseBeforeOffset: CartesianPose | undefined;

      if (args.transform) {
        poseBeforeOffset = pose;
        pose = applyUserDefinedTransform(args.transform, pose, effectivePlane);
      }

      const result = {
        // The result carries the detector BOX (Detection2D) and, when
        // segmentation ran, the SAM2 contour echoed back via Detection3D.segment.
        blob: {
          x: detection.blob2d.x,
          y: detection.blob2d.y,
          width: detection.blob2d.width,
          height: detection.blob2d.height,
          rotation: detection.blob2d.rotation,
          score: detection.blob2d.score,
          classId: detection.blob2d.classId,
          className: detection.blob2d.className,
        },
        pose,
        poseBeforeOffset,
        segment: protoSegmentToBlob2D(detection.segment),
        usedTopDownFallback: detection.usedTopDownFallback || undefined,
        orientationSource:
          detection.orientationSource == null
            ? undefined
            : ORIENTATION_SOURCE_BY_ORDINAL[detection.orientationSource],
      };

      projectedPoses.set(result, projectedPose);

      return result;
    })
    .filter(isNotUndefined);

  log.info(
    'blobs.transformed',
    'Transformed blobs',
    blobsAndPoses.map(stripSegmentForLog),
  );

  const sizedBlobsAndPoses = depthAreaBounds
    ? filterProjectedMatchesByArea(blobsAndPoses, depthAreaBounds, {
        intrinsics,
        cameraPose: cameraPoseAtCapture(extrinsics, wristPoseAtCapture),
        surfacePoseOf: (match) => projectedPoses.get(match) ?? match.pose,
      }).slice(0, resultsLimit)
    : blobsAndPoses;

  // `world-z-desc` orders by each match's projected height, which only exists
  // now that poses are back from projection. Every other ordering was already
  // settled by `sortBlobs` on the 2D blobs above and passes through unchanged.
  const results = applyPostProjectionOrdering(
    sizedBlobsAndPoses,
    args.resultOrdering,
  );

  return {
    results,
    image,
    debugVisualization: response.data.debugVisualization,
    heatmap,
    cameraPose: cameraPoseAtCapture(extrinsics, wristPoseAtCapture),
  };
}
